/* Benchmark Golf PDP — ported from the static site (main.js + product.js).
   Gallery sync, rails, accordions, countdown, delivery dates, sticky bars,
   show-more toggles, lazy videos, and Shopify cart wiring for [data-bm-atc]. */
(() => {
  'use strict';

  // benchmark-pdp-main, bundle-offer-benchmark and benchmark-pad-main each carry their
  // own <script src> so they still work standalone, so this file executes once per
  // section on a page that renders more than one. Without this guard every listener
  // binds twice — which made the buy-box accordions dead (two handlers toggling the
  // same class cancel out) and left add-to-cart relying on _CART_ADD_IN_PROGRESS to
  // swallow the duplicate POST.
  if (window.__benchmarkPdpInit) return;
  window.__benchmarkPdpInit = true;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  // gallery slides are a mix of bare <img> and <picture>-wrapped mobile art
  // direction, so every rail query has to reach through the wrapper
  /* the hero slide is a <video>, so it counts as a slide alongside the
     images - without this the dots, thumbs and wrap all drift by one */
  // the overhead slide is a <figure> (photo plus the traced 7x7 overlay), so the
  // figure itself is the slide there - its img is not a rail flex item
  const SLIDE_SEL = ':scope > img, :scope > picture > img, :scope > video, :scope > figure';

  /* GAH-PHOTO-01: the Local Party slide, thumb and dot ship in the markup
     behind html.gd-party-photo (gate at the end of assets/benchmark-pdp.css).
     The arm lands 2.5-5s after first paint, so every index and count below
     comes from the LIVE list at call time. Only [data-gd-party] is ever
     filtered, never visibility: a hero-still hidden video stays at index 0
     exactly as before, quirks included. */
  const partyOn = () => document.documentElement.classList.contains('gd-party-photo');
  /* The test is a SWAP: photo 2 is [data-gd-studio] in the control arm and
     [data-gd-party] in arm B, so exactly one of the pair is on the page and
     the live list is seven slides either way. Filter by the arm, never by
     visibility - a hero-still hidden video stays at index 0 exactly as before. */
  /* GAH-PZ-01 arm C: the picked reason's photo goes in front of the gallery
     as its own slide, thumb and dot, all [data-pz-slide] (snippets/benchmark-pz).
     They ship on the live template for every visitor, display:none and out of
     the gallery until C's visitor picks a reason; then they are photo 1 and the
     seven photos follow unchanged. A and B pick too, but never touch the
     gallery, so the filter keys on html.gd-pz-photo (C only) as well. */
  const pzPicked = () =>
    document.documentElement.classList.contains('gd-pz-photo') &&
    Boolean(document.querySelector('.bm-pdp > input.pz-radio:checked'));
  const live = (list) => {
    const arm = partyOn()
      ? list.filter((el) => !el.closest('[data-gd-studio]'))
      : list.filter((el) => !el.closest('[data-gd-party]'));
    return pzPicked() ? arm : arm.filter((el) => !el.closest('[data-pz-slide]'));
  };

  // the PDP renders as two sections, each with its own .bm-pdp wrapper —
  // query across all of them, never just the first
  if (!$('.bm-pdp')) return;

  /* ---------- Toast ---------- */
  let toastEl = null;
  let toastTimer = null;
  const toast = (msg) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bm-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), 2600);
  };

  /* ---------- Countdown [data-countdown] — ends midnight, restarts daily ---------- */
  const countdowns = $$('.bm-pdp [data-countdown]');
  if (countdowns.length) {
    /* the header's [data-sale-deadline] is the site's single source of truth
       for when the sale ends; without it this rolls to midnight as before */
    const dl = document.querySelector('[data-sale-deadline]');
    const realAt = dl && dl.dataset.saleDeadline ? new Date(dl.dataset.saleDeadline).getTime() : NaN;
    const real = Number.isNaN(realAt) ? null : realAt;
    let end = new Date();
    end.setHours(23, 59, 59, 999);
    const pad = (n) => String(n).padStart(2, '0');
    let countdownInterval = null;
    const tick = () => {
      let diff;
      if (real !== null) {
        diff = Math.max(0, real - Date.now());
        /* Same fix as the bundle card (benchmark-launch-bundle.js) and the
           header bar (header-benchmark.js): a REAL deadline that has passed
           must not sit at 00:00:00. Hide the element rather than lie. */
        if (diff <= 0) {
          countdowns.forEach((el) => { el.hidden = true; });
          if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
          return;
        }
      } else {
        diff = end - Date.now();
        if (diff <= 0) {
          end = new Date();
          end.setHours(23, 59, 59, 999);
          diff = end - Date.now();
        }
      }
      const d = Math.floor(diff / 8.64e7);
      const h = Math.floor((diff % 8.64e7) / 3.6e6);
      const m = Math.floor((diff % 3.6e6) / 6e4);
      const s = Math.floor((diff % 6e4) / 1000);
      const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
      const text = d > 0 ? `${d}d ${clock}` : clock;
      countdowns.forEach((el) => (el.textContent = text));
    };
    tick();
    countdownInterval = setInterval(tick, 1000);
  }

  /* ---------- Accordions (buy-box mini + what-you-get) ---------- */
  $$('.bm-pdp .acc-item').forEach((item) => {
    const trigger = $('.acc-trigger', item);
    trigger?.addEventListener('click', () => {
      const group = item.closest('[data-acc-single]');
      if (group && !item.classList.contains('open')) {
        $$('.acc-item.open', group).forEach((o) => {
          o.classList.remove('open');
          $('.acc-trigger', o)?.setAttribute('aria-expanded', 'false');
        });
      }
      item.classList.toggle('open');
      trigger.setAttribute('aria-expanded', item.classList.contains('open'));
    });
  });

  /* ---------- Rails: pointer drag (capture-scoped) + arrow nav ---------- */
  $$('.bm-pdp .rail').forEach((rail) => {
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    rail.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      moved = false;
      startX = e.clientX;
      startScroll = rail.scrollLeft;
      rail.setPointerCapture(e.pointerId);
    });

    rail.addEventListener('pointermove', (e) => {
      if (!rail.hasPointerCapture?.(e.pointerId)) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 5) {
        moved = true;
        rail.classList.add('is-dragging');
      }
      rail.scrollLeft = startScroll - dx;
    });

    const endDrag = (e) => {
      if (rail.hasPointerCapture?.(e.pointerId)) rail.releasePointerCapture(e.pointerId);
      rail.classList.remove('is-dragging');
    };
    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);

    rail.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    });

    const wrap = rail.closest('.rail-wrap');
    if (wrap) {
      const prev = $('.rail-nav--prev', wrap);
      const next = $('.rail-nav--next', wrap);
      const isGallery = rail.hasAttribute('data-gallery-rail');
      // gallery pages one exact slide stride; content rails page 75% of the viewport
      const page = () => {
        if (isGallery) {
          // wrap clones are display:none on desktop (offsetLeft 0) — the
          // stride has to come from the real slides
          const imgs = live([...rail.querySelectorAll(SLIDE_SEL)].filter((im) => !im.closest('[data-gallery-clone]')));
          /* GAH-PZ: measured on slides that have a box. The hero-still arm keeps
             a display:none video in the list (offsetLeft 0), and with the pz
             reason slide in front of it the raw [1]-[0] went negative */
          const shown = imgs.filter((el) => el.offsetWidth > 0);
          return shown.length > 1 ? shown[1].offsetLeft - shown[0].offsetLeft : rail.clientWidth;
        }
        return rail.clientWidth * 0.75;
      };

      // figma 34516:92332 shows both gallery arrows on slide 1, so the gallery wraps
      // instead of hiding the arrow it has run out of room for
      /* GAH-PZ: the gallery steps to the neighbouring slide's own left edge,
         not by a fixed stride. The rail's slides are a mix of <picture>
         (display: contents, whose img gets the flex gap on both sides) and
         <figure> (the 7x7 overhead, the pz reason slide), and the two sit
         24px apart in stride, so scrollBy(stride) landed a slide 24px off its
         snap point after every figure and showed the neighbour's edge. */
      const slideLefts = () => {
        const shown = live([...rail.querySelectorAll(SLIDE_SEL)].filter((im) => !im.closest('[data-gallery-clone]'))).filter((el) => el.offsetWidth > 0);
        return shown.map((el) => el.offsetLeft - rail.offsetLeft);
      };
      const step = (dir) => {
        const max = rail.scrollWidth - rail.clientWidth - 4;
        if (isGallery) {
          const lefts = slideLefts();
          if (!lefts.length) return;
          const here = rail.scrollLeft;
          let target;
          if (dir > 0) {
            target = lefts.find((x) => x > here + 4);
            /* past the last photo the rail wraps to photo 1, whose own left
               can sit a gap past 0 behind the leading wrap clone; 0 is the
               rail's true start, where the thumb and dot observers expect it */
            if (target === undefined) target = 0;
          } else {
            const back = lefts.filter((x) => x < here - 4);
            target = back.length ? back[back.length - 1] : lefts[lefts.length - 1];
          }
          /* the rail cannot scroll past its own end: a last slide whose left
             sits beyond scrollWidth - clientWidth stops at the max, which can
             be a gap short of its edge. Land on the max then, so the slide is
             flush and the observer marks it. */
          const limit = rail.scrollWidth - rail.clientWidth;
          rail.scrollTo({ left: Math.min(target, limit), behavior: 'smooth' });
          return;
        }
        if (dir < 0 && rail.scrollLeft <= 4) {
          rail.scrollTo({ left: max, behavior: 'smooth' });
        } else if (dir > 0 && rail.scrollLeft >= max) {
          rail.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          rail.scrollBy({ left: dir * page(), behavior: 'smooth' });
        }
      };

      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));

      const updateNav = () => {
        if (isGallery) return;
        const max = rail.scrollWidth - rail.clientWidth - 4;
        prev?.toggleAttribute('disabled', rail.scrollLeft <= 4);
        next?.toggleAttribute('disabled', rail.scrollLeft >= max);
      };
      rail.addEventListener('scroll', updateNav, { passive: true });
      updateNav();
    }
  });

  /* ---------- Content rails: swipe position dots ---------- */
  // Play at Home is a static four-across grid on desktop and a swipe below
  // 990px. The dots are the only thing telling you there are four modes, so
  // they follow the rail's scroll position rather than a click handler — a
  // flick, a drag and a momentum fling all have to move them.
  $$('.bm-pdp [data-rail-dots]').forEach((dots) => {
    const wrap = dots.closest('.rail-wrap');
    const railEl = wrap && $('.rail', wrap);
    const marks = $$('span', dots);
    if (!railEl || !marks.length) return;

    // card centres, in the rail's own scroll coordinates. offsetLeft is
    // measured from .rail-wrap (the positioned ancestor) and ignores scroll,
    // so subtracting the rail's own offset lands in the same space scrollLeft
    // uses — the frame of reference the gallery wrap-around already works in.
    // Cached because reading offsetLeft inside the scroll handler would force
    // a layout on every frame of an iOS momentum fling.
    let centres = [];
    let port = 0;
    let maxScroll = 0;

    const paint = () => {
      const x = railEl.scrollLeft;
      let active;
      // Nearest centre is right in the middle of the rail but wrong at its
      // ends: on a tablet two cards are still on screen when the rail runs out
      // of scroll, so the last card never reaches the centre and its dot would
      // stay dark however far you swipe. Both ends resolve by position instead.
      if (x <= 1 || maxScroll <= 1) {
        active = 0;
      } else if (x >= maxScroll - 1) {
        active = centres.length - 1;
      } else {
        active = 0;
        let closest = Infinity;
        centres.forEach((c, i) => {
          const gap = Math.abs(c - (x + port / 2));
          if (gap < closest) {
            closest = gap;
            active = i;
          }
        });
      }
      marks.forEach((m, i) => m.classList.toggle('is-active', i === active));
    };

    const measure = () => {
      port = railEl.clientWidth;
      maxScroll = railEl.scrollWidth - port;
      centres = [...railEl.children].map(
        (card) => card.offsetLeft - railEl.offsetLeft + card.offsetWidth / 2
      );
      paint();
    };

    railEl.addEventListener('scroll', paint, { passive: true });
    // crossing 990px swaps the grid for the rail, which moves every centre
    window.addEventListener('resize', measure, { passive: true });
    // late webfonts and lazy tile art can settle after DOMContentLoaded
    window.addEventListener('load', measure, { passive: true });
    // GAH-MODES-02 ships a second rail in #play that is display:none until an
    // arm class lands, 2.5-5 s after paint and after every hook above has
    // fired - its centres would stay at the zeros measured while it had no
    // box, and the first dot would never move. A rail that gains or loses its
    // box resizes, so this re-measures it whichever way the arm flips.
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(railEl);
    measure();
  });

  /* ---------- Gallery: dots + thumbs sync + mobile wrap-around ---------- */
  const rail = $('.bm-pdp [data-gallery-rail]');
  if (rail) {
    // mobile swipe wraps: clone the last slide to the front and the first to
    // the back, then snap-jump to the twin real slide once the scroll settles
    // on a clone. Desktop hides the clones (CSS) and keeps its arrow wrap.
    /* only slides are cloned: a non-slide child has no box, and when the
       poster preload <link> sat first in the GAH rail it became the "slide 1"
       clone, left nothing after photo 7 and killed the forward wrap */
    const originals = [...rail.children].filter((el) => el.matches('img, picture, video, figure'));
    let cloneFirst = null;
    let cloneLast = null;
    /* everything below this file's gallery - [data-bm-atc] included - wires
       after this block, and __benchmarkPdpInit is already set, so a throw here
       would leave Add to Cart dead. Any failure falls back to no wrap. */
    let headClone = null;
    let tailClone = null;
    try {
      if (originals.length > 1) {
        /* GAH-PZ-01: arm C's reason slide sits first in the rail but is not
           photo 1 until a pick, so the wrap's twin is the first real slide (the
           hero) for every visitor; benchmark-pz.js points it at the reason
           photo once C's visitor picks one */
        const head = originals.find((el) => !el.matches('[data-pz-slide]')) || originals[0];
        tailClone = originals[originals.length - 1].cloneNode(true);
        /* the GAH hero slide is a bare <video>. Never cloneNode it: inserting
           its <source>s runs resource selection even on a detached copy, which
           can start a second fetch of the clip. Build the still straight from
           its poster (already preloaded) instead - no data-pdp-hero-video, so
           the hero harness below never picks it up. */
        if (head.matches('video')) {
          headClone = document.createElement('img');
          headClone.src = head.getAttribute('poster') || '';
          headClone.alt = '';
          headClone.className = head.className;
        } else {
          headClone = head.cloneNode(true);
        }
        [headClone, tailClone].forEach((clone) => {
          clone.setAttribute('data-gallery-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          // every image slide is wrapped in a <picture>, but a hero slide can be
          // a bare media element, so a clone can also BE the img
          const cloneImgs = clone.matches('img') ? [clone] : [...clone.querySelectorAll('img')];
          /* the head clone repeats slide 1 (the poster on GAH), already in
             cache, so eager is free. The tail clone (the last photo again -
             photo 7 on GAH, its heaviest file) stays lazy:
             desktop hides every clone and a display:none eager img still
             downloads, and phones warm it on the first touch of the gallery
             (warmTail below) instead of at DOMContentLoaded. */
          cloneImgs.forEach((img) => {
            img.loading = clone === tailClone ? 'lazy' : 'eager';
            img.removeAttribute('fetchpriority');
          });
          /* the wrap clones the hero slide, which would otherwise decode and play
             the video two extra times. Swap each cloned video for its own poster:
             the wrap still looks right and only one video ever runs. */
          const cloneVids = clone.matches('video') ? [clone] : [...clone.querySelectorAll('video')];
          cloneVids.forEach((vid) => {
            const still = document.createElement('img');
            still.src = vid.getAttribute('poster') || '';
            still.alt = '';
            still.loading = 'eager';
            still.className = vid.className;
            if (vid.parentNode) vid.parentNode.replaceChild(still, vid);
          });
        });
        rail.prepend(tailClone);
        rail.append(headClone);
        cloneFirst = headClone;
        cloneLast = tailClone;
      }
    } catch (e) {
      if (tailClone && tailClone.parentNode) tailClone.remove();
      if (headClone && headClone.parentNode) headClone.remove();
      cloneFirst = null;
      cloneLast = null;
    }

    const allSlides = $$(SLIDE_SEL, rail);
    const slides = allSlides.filter((s) => !s.closest('[data-gallery-clone]'));
    const dots = $$('.bm-pdp .pdp-dots span');
    const thumbs = $$('.bm-pdp .pdp-thumb');
    const mobileGallery = matchMedia('(max-width: 989.98px)');
    let current = 0;
    let slide2Seen = false;

    /* the lazy tail clone sits one stride left of photo 1, where a backward
       swipe lands. Warm it on the first touch of the gallery - phones only,
       since desktop never shows a clone - and unbind only once it has
       actually warmed, so a tablet that starts wide and rotates still gets it. */
    if (cloneLast) {
      const warmTail = () => {
        if (!mobileGallery.matches) return;
        try {
          (cloneLast.matches('img') ? [cloneLast] : $$('img', cloneLast)).forEach((img) => { img.loading = 'eager'; });
        } catch (e) {}
        rail.removeEventListener('touchstart', warmTail);
        rail.removeEventListener('pointerdown', warmTail);
      };
      rail.addEventListener('touchstart', warmTail, { passive: true });
      rail.addEventListener('pointerdown', warmTail, { passive: true });
    }

    /* slides 3-7 are lazy inside a horizontal scroller, and WebKit (every iOS
       in-app browser) only requests one as it slides in, so a swipe could land
       on a grey box. Once the visitor engages the gallery - first touch, click
       or thumb - keep the next two live slides warm. IMGs from the live list
       only: never the video, a clone, or the photo-2 twin that is not live. */
    let galleryEngaged = false;
    const warmSlide = (el) => {
      const img = el && (el.tagName === 'IMG' ? el : el.querySelector('img'));
      if (img && img.loading === 'lazy') img.loading = 'eager';
    };
    const warmAhead = (i) => {
      try {
        const list = live(slides);
        warmSlide(list[i + 1]);
        warmSlide(list[i + 2]);
      } catch (e) {}
    };
    const engageGallery = () => {
      if (galleryEngaged) return;
      galleryEngaged = true;
      warmAhead(current);
    };
    const galleryStage = rail.closest('.rail-wrap') || rail;
    galleryStage.addEventListener('touchstart', engageGallery, { passive: true, once: true });
    galleryStage.addEventListener('pointerdown', engageGallery, { passive: true, once: true });

    const setActive = (i) => {
      current = i;
      live(dots).forEach((d, k) => d.classList.toggle('is-active', k === i));
      live(thumbs).forEach((t, k) => {
        t.classList.toggle('is-active', k === i);
        if (k === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      /* GAH-PHOTO-01: once per pageview, the first time live slide 2 is the
         active slide, in BOTH arms, only while the test is on. The latch is
         not set while photoTest is still false - ig lands late. */
      if (i === 1 && !slide2Seen && window.GD_FLAGS && window.GD_FLAGS.photoTest === true) {
        slide2Seen = true;
        const arm = partyOn() ? 'b_party' : 'a_control';
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'gah_photo_slide2_view', gah_photo_arm: arm });
        } catch (e) {}
        try { if (window.clarity) window.clarity('event', 'gah_photo_slide2_view'); } catch (e) {}
      }
      if (galleryEngaged) warmAhead(i);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const list = live(slides);
          let i = list.indexOf(entry.target);
          if (i === -1) {
            // a clone stands in for the opposite real end
            i = entry.target.closest('[data-gallery-clone]') === cloneFirst ? 0 : list.length - 1;
          }
          setActive(i);
        });
      },
      { root: rail, threshold: 0.6 }
    );
    allSlides.forEach((s) => io.observe(s));

    const snapX = (img) => img.offsetLeft - rail.offsetLeft - (rail.clientWidth - img.offsetWidth) / 2;
    const jumpTo = (img) => { rail.scrollLeft = snapX(img); };

    /* GAH-GIF-IAB-01: an iOS browser that refuses play() gets the hero swapped
       for an image after this ran (benchmark-pdp-main.liquid). Put the image in
       the video's place in the slide lists, and re-park the rail on it: WebKit
       re-snaps to the start when the snapped slide leaves the DOM. */
    document.addEventListener('gd:hero-image', (event) => {
      const { video, img } = event.detail || {};
      if (!video || !img) return;
      [allSlides, slides].forEach((list) => {
        const k = list.indexOf(video);
        if (k > -1) list[k] = img;
      });
      io.unobserve(video);
      io.observe(img);
      if (cloneLast && mobileGallery.matches && live(slides)[current] === img) jumpTo(img);
    });

    if (cloneLast) {
      // the prepended clone shifts everything one stride right — start on the
      // real first slide (desktop keeps scrollLeft 0: its clones render none)
      const jumpToStart = () => { if (mobileGallery.matches) jumpTo(live(slides)[0]); };
      jumpToStart();
      requestAnimationFrame(jumpToStart);
      mobileGallery.addEventListener('change', () =>
        requestAnimationFrame(() => jumpTo(live(slides)[Math.max(0, current)]))
      );

      // settle detection: snap/momentum keeps emitting scroll events; when they
      // stop while resting on a clone, jump (instantly, same pixels) to its twin
      let settleTimer = 0;
      rail.addEventListener(
        'scroll',
        () => {
          if (!mobileGallery.matches) return;
          clearTimeout(settleTimer);
          settleTimer = setTimeout(() => {
            const list = live(slides);
            const first = list[0];
            const last = list[list.length - 1];
            /* GAH-PZ: a hero-still video is display:none at offsetLeft 0; with the
               pz reason slide in front of it the raw [1]-[0] stride went negative
               and the wrap bounced between photo 1 and photo 7 forever. Phone
               slides are the rail's width with no gap, so that is the stride
               whenever the neighbours do not give a positive one. */
            const rawStride = list.length > 1 ? list[1].offsetLeft - first.offsetLeft : 0;
            const stride = rawStride > 0 ? rawStride : rail.clientWidth;
            const x = rail.scrollLeft;
            if (x < snapX(first) - stride / 2) jumpTo(last);
            else if (x > snapX(last) + stride / 2) jumpTo(first);
          }, 100);
        },
        { passive: true }
      );
    }

    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        // GAH-PHOTO-01: the live position of the thumb, not data-slide-to
        const i = live(thumbs).indexOf(t);
        const target = live(slides)[i];
        if (!target) return;
        engageGallery();
        warmSlide(target);
        rail.scrollTo({ left: target.offsetLeft - rail.offsetLeft, behavior: 'smooth' });
      });
    });

    /* GAH-PHOTO-01 follower. theme.liquid apply() calls this whenever the arm
       lands (2.5-5s after paint on ig traffic), so it queries the DOM fresh and
       is idempotent. (1) aria-labels count from the LIVE list, so control keeps
       "Photo 1".."Photo 7" and arm B reads "Photo 1".."Photo 8"; (2) once the
       class is on the party img goes eager so it is decoded before the swipe. */
    const applyPhoto = () => {
      const liveThumbs = live($$('.bm-pdp .pdp-thumb'));
      liveThumbs.forEach((t, k) => t.setAttribute('aria-label', `Photo ${k + 1}`));
      /* Both swap thumbs ship labelled "Photo 2" - right for whichever arm is
         live, and the label to fall back on if this file never executes. The
         one that is not live is display:none (already out of the a11y tree),
         but leaving the name on it puts a second "Photo 2" in the DOM next to
         the real one, which reads as a duplicate to anything walking the
         markup rather than the a11y tree. Drop the name while it is not live. */
      $$('.bm-pdp .pdp-thumb[data-gd-party], .bm-pdp .pdp-thumb[data-gd-studio]').forEach((t) => {
        if (liveThumbs.indexOf(t) !== -1) { t.removeAttribute('aria-hidden'); return; }
        t.removeAttribute('aria-label');
        t.setAttribute('aria-hidden', 'true');
      });
      if (partyOn()) $$('.bm-pdp .pdp-rail [data-gd-party] img').forEach((img) => { img.loading = 'eager'; });
      /* since the 09-18 rollout the studio slide ships lazy (hidden for
         everyone), so a ?gd_photo=off view still needs its photo 2 eager */
      else $$('.bm-pdp .pdp-rail [data-gd-studio] img').forEach((img) => { img.loading = 'eager'; });
    };
    window.GD_FLAGS = window.GD_FLAGS || {};
    window.GD_FLAGS.applyPhoto = applyPhoto;
    applyPhoto();

    // the sticky gallery rides out of its section at the end of the hero, which
    // used to carry the arrows off behind the header; keep them pinned inside
    // whatever slice of the stage is still on screen (figma sits them at
    // 49.12% of the image, so that stays the resting point)
    const stage = rail.closest('.pdp-stage');
    const nav = stage ? $$('.rail-nav', stage) : [];
    const header = document.querySelector('.section-header-benchmark');
    if (stage && nav.length) {
      const desktop = matchMedia('(min-width: 990px)');
      let queued = false;
      const placeArrows = () => {
        queued = false;
        if (!desktop.matches) {
          nav.forEach((b) => { b.style.top = ''; b.style.opacity = ''; b.style.pointerEvents = ''; });
          return;
        }
        const s = stage.getBoundingClientRect();
        const visTop = Math.max(s.top, header ? header.getBoundingClientRect().bottom : 0);
        const visBottom = Math.min(s.bottom, window.innerHeight);
        if (visBottom - visTop < 96) {
          nav.forEach((b) => { b.style.opacity = '0'; b.style.pointerEvents = 'none'; });
          return;
        }
        const ideal = s.top + s.height * 0.4912;
        const center = Math.min(Math.max(ideal, visTop + 40), visBottom - 40);
        nav.forEach((b) => {
          b.style.opacity = '';
          b.style.pointerEvents = '';
          b.style.top = `${center - s.top}px`;
        });
      };
      const requestPlace = () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(placeArrows);
        }
      };
      window.addEventListener('scroll', requestPlace, { passive: true });
      window.addEventListener('resize', requestPlace);
      desktop.addEventListener('change', requestPlace);
      // the header shrinks on scroll, and its transition settles after the
      // scroll events stop firing — re-place once it lands
      if (header && 'ResizeObserver' in window) new ResizeObserver(requestPlace).observe(header);
      placeArrows();
    }
  }

  /* ---------- Gallery: 7x7 footprint loop on the overhead slide ---------- */
  // the tape only animates while its slide is actually visible - inside the
  // rail's viewport and inside the page's, since a root-less observer clips
  // through the rail's overflow - so it never loops behind another slide or
  // below the fold. queried after the wrap clones exist so a cloned copy of the
  // slide gets its own toggle too. reduced motion shows the settled outline via css.
  // the bento 7x7 tile carries the same hook, so the slide, its clones and the
  // tile share one observer
  /* ---------- GAH-PHONE-01: the "Works With Just a Phone" bento tile ----------
     The tile ships display:none with src-less images. Intelligems lands 2.5-5 s
     after paint, so nothing here is decided at init: a MutationObserver on the
     root class re-runs the loader whenever an arm class lands.
     B (static) loads one still. C (motion) paints the loop's first frame as a
     still, then swaps to the animated webp once it has fully downloaded and
     the tile is one viewport away - the 52% who never scroll pay nothing,
     and nobody sees a half-loaded loop. prefers-reduced-motion keeps the still.
     Events (all gated on phoneTest, all once per pageview):
       gah_phone_zone_view  the hero tile's bottom edge came on screen. That
                            edge is identical in all three arms, so this is the
                            arm-independent denominator for B vs C.
       gah_phone_tile_view  the tile was >= 50% visible for 500 ms (B, C only).
       gah_phone_tile_dwell bucketed time the tile spent >= 50% visible, sent
                            when it leaves the screen or the page hides. */
  const phoneTile = $('.bm-pdp [data-gd-phone]');
  const phoneHero = $('.bm-pdp .bento-grid .b-sim');
  if (phoneTile && phoneHero) {
    const root = document.documentElement;
    const flags = () => window.GD_FLAGS || {};
    const seen = { zone: false, tile: false };
    const track = (name, tags) => {
      if (!flags().phoneTest) return;
      const payload = Object.assign({ gah_phone_arm: flags().phoneArm ? flags().phoneArm() : 'a_control' }, tags || {});
      if (flags().track) flags().track(name, payload);
    };
    const still = $('.b-phone-still', phoneTile);
    const spin = $('.b-phone-spin', phoneTile);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animRequested = false;

    /* half a viewport of lead past the fold (the bento starts ~1,330px down on a
       phone, so a bigger lead would fetch on load): enough to finish ~1 MB before the tile scrolls in,
       not so much that the visitors who never scroll pay for it */
    const nearViewport = () => phoneTile.getBoundingClientRect().top < window.innerHeight * 1.5;
    const loadPhoneMedia = () => {
      if (!root.classList.contains('gd-phone-tile')) return;
      const motion = root.classList.contains('gd-phone-motion');
      const img = motion ? spin : still;
      if (img && !img.getAttribute('src')) img.src = img.dataset.gdPhoneSrc;
      if (!motion || animRequested || reduced.matches || !nearViewport()) return;
      animRequested = true;
      const url = window.matchMedia('(max-width: 989.98px)').matches ? spin.dataset.gdPhoneAnimMobile : spin.dataset.gdPhoneAnim;
      const anim = new Image();
      anim.onload = () => {
        spin.src = url;
        spin.dataset.gdPhoneState = 'anim';
      };
      anim.onerror = () => { spin.dataset.gdPhoneState = 'error'; };
      anim.src = url;
    };

    new MutationObserver(loadPhoneMedia).observe(root, { attributes: true, attributeFilter: ['class'] });
    loadPhoneMedia();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        loadPhoneMedia();
        if (!seen.zone && flags().phoneTest && phoneHero.getBoundingClientRect().bottom <= window.innerHeight) {
          seen.zone = true;
          window.GD_FLAGS.phoneSeen = seen;
          track('gah_phone_zone_view');
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if ('IntersectionObserver' in window) {
      let visibleSince = 0;
      let dwell = 0;
      let viewTimer = 0;
      let dwellSent = false;
      const bucket = (ms) => (ms < 1000 ? '0-1s' : ms < 3000 ? '1-3s' : ms < 6000 ? '3-6s' : '6s+');
      const sendDwell = () => {
        if (visibleSince) { dwell += Date.now() - visibleSince; visibleSince = 0; }
        if (dwellSent || !seen.tile) return;
        dwellSent = true;
        track('gah_phone_tile_dwell', {
          gah_phone_dwell: bucket(dwell),
          gah_phone_media: spin && spin.dataset.gdPhoneState === 'anim' ? 'anim' : 'still',
        });
      };
      new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!visibleSince) visibleSince = Date.now();
          if (!seen.tile && !viewTimer) {
            viewTimer = window.setTimeout(() => {
              viewTimer = 0;
              if (!visibleSince || !flags().phoneTest) return;
              seen.tile = true;
              window.GD_FLAGS.phoneSeen = seen;
              track('gah_phone_tile_view', { gah_phone_rm: reduced.matches ? 1 : 0 });
            }, 500);
          }
        } else {
          window.clearTimeout(viewTimer);
          viewTimer = 0;
          sendDwell();
        }
      }, { threshold: [0, 0.5] }).observe(phoneTile);
      window.addEventListener('pagehide', sendDwell);
    }
  }

  /* ---------- GAH-PEEK-01: the reviews peek ----------
     The buy-box rating link is a real #reviews anchor. Intelligems lands 2.5-5 s
     after paint, so nothing is decided at init: the arm is read off the root
     classes at CLICK time. Without html.gd-peek the click is today's jump (and
     is counted, so control's jump rate is known); with it the same click opens
     the box under the stars. Media has no src until the first open, so control
     and unopened arms download nothing. After opening, the page scrolls only
     as far as it must to keep Add to Cart on screen, never pushing the stars
     off the top.
     Events (all gated on peekTest, each once per pageview):
       gah_peek_jump        arm A (or any render without the box): rating tapped
       gah_peek_open        the box opened
       gah_peek_swipe       first sideways move past card one
       gah_peek_end         the last card came into view
       gah_peek_more        a long written review was expanded
       gah_peek_video_play  a muted loop started
       gah_peek_video_tap   a clip was tapped for the full review with sound
       gah_peek_close       the box closed; carries the deepest card seen */
  const peekBox = $('.bm-pdp [data-gd-peek]');
  const peekLink = $('.bm-pdp [data-gd-peek-link]');
  if (peekBox && peekLink) {
    const root = document.documentElement;
    const flags = () => window.GD_FLAGS || {};
    const row = peekLink.closest('.bb-rating');
    const prev = $('[data-gd-peek-prev]', peekBox);
    const next = $('[data-gd-peek-next]', peekBox);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sent = {};
    const seen = { opened: false, depth: 0 };
    window.GD_FLAGS = window.GD_FLAGS || {};
    window.GD_FLAGS.peekSeen = seen;
    let open = false;

    const armed = () => root.classList.contains('gd-peek');
    const track = (name, tags) => {
      if (sent[name] || !flags().peekTest) return;
      sent[name] = true;
      if (flags().track) flags().track(name, Object.assign({ gah_peek_arm: flags().peekArm ? flags().peekArm() : 'a_control' }, tags || {}));
    };
    /* the rail the arm class is showing right now (a late flip can change it) */
    const rail = () => $$('[data-gd-peek-rail]', peekBox).find((el) => el.offsetParent !== null) || null;

    /* only the rail this arm shows ever gets its images */
    const loadMedia = () => {
      const el = rail();
      if (!el) return;
      $$('img[data-gd-peek-src]', el).forEach((img) => { if (!img.getAttribute('src')) img.src = img.dataset.gdPeekSrc; });
    };

    const index = (el) => {
      const cards = Array.from(el.children);
      if (!cards.length) return 0;
      if (el.scrollLeft > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) return cards.length - 1;
      let idx = 0;
      cards.forEach((card, i) => { if (card.offsetLeft <= el.scrollLeft + 24) idx = i; });
      return idx;
    };
    const syncNav = () => {
      const el = rail();
      if (!el) return;
      prev.disabled = el.scrollLeft < 4;
      next.disabled = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      const depth = index(el) + 1;
      if (depth > seen.depth) seen.depth = depth;
      if (depth > 1) track('gah_peek_swipe');
      if (depth === el.children.length && el.children.length > 1) track('gah_peek_end');
    };

    /* clips: the muted 5 s loop plays only while its card is mostly on screen
       inside the rail. a tap swaps in the full Judge.me review with sound; a
       second tap, or the end of it, goes back to the loop. */
    const stopFull = (card) => {
      const video = $('video', card);
      if (!card.classList.contains('is-full')) return;
      card.classList.remove('is-full');
      video.pause();
      video.muted = true;
      video.loop = true;
      video.src = video.dataset.gdPeekSrc;
    };
    const syncVideos = () => {
      const el = rail();
      $$('.bb-peek-card--video', peekBox).forEach((card) => {
        const video = $('video', card);
        if (card.classList.contains('is-full')) {
          if (!open) stopFull(card);
          return;
        }
        let vis = 0;
        if (open && el && el.contains(card)) {
          const r = card.getBoundingClientRect();
          const rr = el.getBoundingClientRect();
          vis = Math.max(0, Math.min(r.right, rr.right) - Math.max(r.left, rr.left)) / (r.width || 1);
        }
        if (vis >= 0.6 && !reduced.matches && card.dataset.gdPeekPaused !== 'true') {
          if (!video.getAttribute('src')) video.src = video.dataset.gdPeekSrc;
          const playing = video.play();
          if (playing && playing.then) playing.then(() => track('gah_peek_video_play')).catch(() => {});
        } else {
          video.pause();
        }
      });
    };
    $$('.bb-peek-card--video video', peekBox).forEach((video) => {
      const card = video.closest('.bb-peek-card--video');
      video.addEventListener('playing', () => card.classList.add('is-playing'));
      video.addEventListener('pause', () => card.classList.remove('is-playing'));
      video.addEventListener('ended', () => { stopFull(card); syncVideos(); });
    });

    /* the bottom edge of whatever is pinned to the top of the viewport
       (sticky header, sale bar), measured - not assumed */
    const topInset = () => {
      let el = document.elementFromPoint(window.innerWidth / 2, 1);
      while (el && el !== document.body && el !== root) {
        const pos = window.getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'sticky') return Math.max(0, el.getBoundingClientRect().bottom);
        el = el.parentElement;
      }
      return 0;
    };
    const keepAtcInView = () => {
      const atc = $('.bm-pdp .buybox .bb-atc');
      if (!atc) return;
      const need = atc.getBoundingClientRect().bottom - (window.innerHeight - 14);
      const room = row.getBoundingClientRect().top - (topInset() + 10);
      const delta = Math.max(0, Math.min(need, room));
      if (delta > 1) window.scrollBy({ top: delta, behavior: reduced.matches ? 'auto' : 'smooth' });
    };

    const markClamped = () => {
      $$('.bb-peek-card__body', peekBox).forEach((body) => {
        if (body.dataset.full === 'true') return;
        const more = $('[data-gd-peek-more]', body.closest('.bb-peek-card'));
        if (more) more.hidden = !(body.scrollHeight > body.clientHeight + 2);
      });
    };

    const setOpen = (value) => {
      open = value;
      peekBox.dataset.open = String(value);
      row.dataset.gdPeekOpen = String(value);
      peekLink.setAttribute('aria-expanded', String(value));
      if (value) {
        peekBox.removeAttribute('inert');
        loadMedia();
        markClamped();
        seen.opened = true;
        if (!seen.depth) seen.depth = 1;
        track('gah_peek_open');
      } else {
        peekBox.setAttribute('inert', '');
        track('gah_peek_close', { gah_peek_depth: seen.depth });
      }
      syncVideos();
      window.setTimeout(() => {
        if (open) { keepAtcInView(); syncNav(); syncVideos(); }
      }, reduced.matches ? 30 : 500);
    };

    /* the link only becomes a disclosure button while an arm is rendered; a
       late Intelligems flip (either direction) re-syncs it */
    const syncRole = () => {
      if (armed()) {
        peekLink.setAttribute('role', 'button');
        peekLink.setAttribute('aria-controls', 'bb-peek');
        peekLink.setAttribute('aria-expanded', String(open));
      } else {
        if (open) setOpen(false);
        peekLink.removeAttribute('role');
        peekLink.removeAttribute('aria-controls');
        peekLink.removeAttribute('aria-expanded');
      }
    };
    syncRole();
    new MutationObserver(() => { syncRole(); if (open) { loadMedia(); markClamped(); syncNav(); syncVideos(); } }).observe(root, { attributes: true, attributeFilter: ['class'] });

    peekLink.addEventListener('click', (event) => {
      if (!armed()) { track('gah_peek_jump'); return; }
      event.preventDefault();
      setOpen(!open);
    });
    peekLink.addEventListener('keydown', (event) => {
      if (armed() && event.key === ' ') { event.preventDefault(); setOpen(!open); }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && open && peekBox.contains(document.activeElement)) {
        setOpen(false);
        peekLink.focus({ preventScroll: true });
      }
    });

    const step = (dir) => {
      const el = rail();
      if (!el || !el.firstElementChild) return;
      el.scrollBy({ left: dir * (el.firstElementChild.offsetWidth + 8), behavior: reduced.matches ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));

    let railTimer = 0;
    $$('[data-gd-peek-rail]', peekBox).forEach((el) => {
      el.addEventListener('scroll', () => {
        window.requestAnimationFrame(syncNav);
        window.clearTimeout(railTimer);
        railTimer = window.setTimeout(syncVideos, 140);
      }, { passive: true });
    });

    peekBox.addEventListener('click', (event) => {
      const more = event.target.closest('[data-gd-peek-more]');
      if (more) {
        const body = $('.bb-peek-card__body', more.closest('.bb-peek-card'));
        const full = body.dataset.full === 'true';
        body.dataset.full = String(!full);
        more.textContent = full ? 'More' : 'Less';
        if (!full) track('gah_peek_more');
        return;
      }
      const card = event.target.closest('.bb-peek-card--video');
      if (!card) return;
      const video = $('video', card);
      if (card.classList.contains('is-full')) {
        stopFull(card);
        card.dataset.gdPeekPaused = 'true';
        return;
      }
      track('gah_peek_video_tap');
      $$('.bb-peek-card--video', peekBox).forEach((other) => { if (other !== card) { stopFull(other); $('video', other).pause(); } });
      card.dataset.gdPeekPaused = 'false';
      card.classList.add('is-full');
      video.loop = false;
      video.muted = false;
      video.src = card.dataset.gdPeekFull;
      const playing = video.play();
      if (playing && playing.catch) playing.catch(() => { stopFull(card); syncVideos(); });
    });
  }

  /* ---------- GAH-COURSES-01: the course count ("80+" vs "1,000+") ----------
     The rewrite itself is assets/benchmark-courses-label.js; this block only
     measures. The buy-box bullet is near the top of the page in both arms, but
     the bento tile title sits ~1,500px down on a phone, so the read that
     isolates the tile is add-to-cart among visitors who had it on screen.
     Event (gated on coursesTest at fire time, once per pageview, both arms):
       gah_courses_tile_view  the courses tile was >= 50% visible for 500 ms. */
  const coursesTile = $('.bm-pdp .bento-grid .b-courses');
  if (coursesTile && 'IntersectionObserver' in window) {
    const flags = () => window.GD_FLAGS || {};
    let viewTimer = 0;
    let tileSeen = false;
    const tileObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        if (tileSeen || viewTimer) return;
        viewTimer = window.setTimeout(() => {
          viewTimer = 0;
          // read at fire time: Intelligems lands 2.5-5 s after paint, and a
          // visitor with no test running must send nothing
          if (!flags().coursesTest) return;
          tileSeen = true;
          window.GD_FLAGS.coursesSeen = { tile: true };
          tileObserver.disconnect();
          if (flags().track) {
            flags().track('gah_courses_tile_view', {
              gah_courses_arm: flags().coursesArm ? flags().coursesArm() : 'c80',
            });
          }
        }, 500);
      } else {
        window.clearTimeout(viewTimer);
        viewTimer = 0;
      }
    }, { threshold: [0, 0.5] });
    tileObserver.observe(coursesTile);
  }

  /* ---------- GAH-GIF-01: the bento opens on two loops ----------
     The pair ships display:none with src-less videos. Intelligems lands 2.5-5 s
     after paint, so nothing is decided at init: a MutationObserver on the root
     class arms the tiles whenever html.gd-gif-open lands. The modes tile plays
     five clips back to back; each runs at 1x through the swing and RATE after
     it (the data-gd-gif-fast ranges), and the bar fills off currentTime like
     the one baked into the coaching file. Only the clip on screen is fetched,
     plus the next one. Nothing plays while the tile is off screen. Reduced
     motion gets the posters and no playback.
     Image mode (GAH-GIF-IAB-01): the Facebook and Instagram in-app browsers on
     iPhone refuse a scripted play(), so both tiles sat on their posters. iOS
     WebKit draws an mp4 set as an <img> src as an animated image, which no
     autoplay rule touches, so those browsers get images from the start and any
     other iOS browser that refuses play() (Low Power Mode) switches over.
     An image cannot be seeked or sped up: each clip runs at 1x for its
     data-gd-gif-len and the bar fills off the clock. The same URL in a new
     <img> carries on mid-clip, so a show adds a ?gdl= key to restart it: two
     loops' worth of keys, then the second loop's keys are reused.
     Events (gated on gifTest, once per pageview):
       gah_gif_zone_view   the bento's first tile bottom edge crossed the
                           viewport - the hero in arm A, the modes tile in B.
       gah_gif_modes_loop  arm B only: the five clips played through once. */
  const gifPair = $('.bm-pdp [data-gd-gif]');
  if (gifPair) {
    const root = document.documentElement;
    const flags = () => window.GD_FLAGS || {};
    const RATE = 1.35;
    const seen = { zone: false, loop: false };
    const track = (name) => {
      if (!flags().gifTest) return;
      if (flags().track) flags().track(name, { gah_gif_arm: flags().gifArm ? flags().gifArm() : 'a_control' });
    };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const ua = navigator.userAgent;
    const iosWebKit = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const metaApp = iosWebKit && /FBAN|FBAV|FB_IAB|FBIOS|Instagram/.test(ua);
    const slot = $('[data-gd-gif-modes]', gifPair);
    const frames = $$('.b-gif-frame', slot);
    const fills = $$('.b-gif-bars em', slot);
    const coach = $('.b-gif-coach', gifPair);
    const videos = $$('video', gifPair);
    const oldHero = $('.bm-pdp .bento-grid .b-sim-video');
    let armed = false;
    let index = 0;
    let modesOnScreen = false;
    let coachOnScreen = false;
    let raf = 0;
    let asImages = false;
    let loops = 0;
    let shownAt = 0;
    let heldFor = 0;

    const load = (video) => {
      if (video.getAttribute('src')) return;
      video.preload = 'auto';
      video.src = video.dataset.gdGifSrc;
    };
    /* the image sits over its video, whose poster shows until it paints */
    const paintImage = (video, key) => {
      const old = $('.b-gif-img', video.parentNode);
      const img = document.createElement('img');
      img.className = 'b-gif-img';
      img.alt = video.getAttribute('aria-label') || '';
      img.decoding = 'async';
      img.src = `${video.dataset.gdGifSrc}${key == null ? '' : `&gdl=${key}`}`;
      if (old) old.replaceWith(img);
      else video.after(img);
    };
    const showImage = () => {
      frames.forEach((frame, n) => {
        frame.classList.toggle('is-on', n === index);
        if (n !== index) {
          const img = $('.b-gif-img', frame);
          if (img) window.setTimeout(() => { if (!frame.classList.contains('is-on')) img.remove(); }, 300);
        }
      });
      fills.forEach((fill, n) => { fill.style.width = n < index ? '100%' : '0'; });
      paintImage($('video', frames[index]), Math.min(loops, 1));
      shownAt = performance.now();
      heldFor = 0;
    };
    const show = () => {
      if (asImages) { showImage(); return; }
      frames.forEach((frame, n) => frame.classList.toggle('is-on', n === index));
      fills.forEach((fill, n) => { fill.style.width = n < index ? '100%' : '0'; });
      const video = $('video', frames[index]);
      load(video);
      load($('video', frames[(index + 1) % frames.length]));
      try { video.currentTime = 0; } catch (error) { /* not seekable yet */ }
      video.playbackRate = 1;
      if (modesOnScreen) video.play().catch(refused);
    };
    const next = () => {
      if (index === frames.length - 1) {
        loops += 1;
        if (!seen.loop) {
          seen.loop = true;
          track('gah_gif_modes_loop');
        }
      }
      index = (index + 1) % frames.length;
      show();
    };
    const tick = () => {
      raf = 0;
      if (!modesOnScreen) return;
      const frame = frames[index];
      if (asImages) {
        const len = Number(frame.dataset.gdGifLen) || 6;
        const t = heldFor + (performance.now() - shownAt) / 1000;
        fills[index].style.width = `${Math.min(100, (t / len) * 100)}%`;
        if (t >= len) next();
        raf = window.requestAnimationFrame(tick);
        return;
      }
      const video = $('video', frame);
      if (video.duration) {
        const t = video.currentTime;
        const fast = (frame.dataset.gdGifFast || '').split(',').some((range) => {
          const [from, to] = range.split('-').map(Number);
          return t >= from && t < to;
        });
        video.playbackRate = fast ? RATE : 1;
        fills[index].style.width = `${Math.min(100, (t / video.duration) * 100)}%`;
        if (video.ended || t >= video.duration - 0.04) next();
      }
      raf = window.requestAnimationFrame(tick);
    };

    const toImages = () => {
      if (asImages) return;
      asImages = true;
      videos.forEach((video) => video.pause());
      if (modesOnScreen) {
        showImage();
        if (!raf) raf = window.requestAnimationFrame(tick);
      }
      if (coachOnScreen) paintImage(coach, null);
    };
    function refused(error) {
      if (iosWebKit && error && error.name === 'NotAllowedError') toImages();
    }

    const arm = () => {
      if (armed || !root.classList.contains('gd-gif-open')) return;
      armed = true;
      /* the hidden hero keeps autoplaying its loop otherwise */
      if (oldHero) oldHero.pause();
      videos.forEach((video) => { video.poster = video.dataset.gdGifPoster; });
      if (reduced.matches || !('IntersectionObserver' in window)) return;
      if (metaApp) asImages = true;
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          modesOnScreen = entry.isIntersecting;
          if (asImages) {
            if (modesOnScreen) {
              if ($('.b-gif-img', frames[index])) shownAt = performance.now();
              else showImage();
              if (!raf) raf = window.requestAnimationFrame(tick);
            } else {
              heldFor += (performance.now() - shownAt) / 1000;
            }
            return;
          }
          const video = $('video', frames[index]);
          if (modesOnScreen) {
            load(video);
            load($('video', frames[(index + 1) % frames.length]));
            video.play().catch(refused);
            if (!raf) raf = window.requestAnimationFrame(tick);
          } else {
            video.pause();
          }
        });
      }, { rootMargin: '200px 0px' }).observe(slot);
      const coachObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          coachOnScreen = entry.isIntersecting;
          if (asImages) {
            if (coachOnScreen && !$('.b-gif-img', coach.parentNode)) paintImage(coach, null);
            return;
          }
          if (coachOnScreen) { load(coach); coach.play().catch(refused); } else coach.pause();
        });
      }, { rootMargin: '200px 0px' });
      coachObserver.observe(coach);
    };
    new MutationObserver(arm).observe(root, { attributes: true, attributeFilter: ['class'] });
    arm();

    let zoneTicking = false;
    const onZoneScroll = () => {
      if (zoneTicking || seen.zone) return;
      zoneTicking = true;
      window.requestAnimationFrame(() => {
        zoneTicking = false;
        if (seen.zone || !flags().gifTest) return;
        const first = root.classList.contains('gd-gif-open') ? $('[data-gd-gif-tile="modes"]', gifPair) : $('.bm-pdp .bento-grid .b-sim');
        if (!first || first.getBoundingClientRect().bottom > window.innerHeight) return;
        seen.zone = true;
        window.GD_FLAGS.gifSeen = seen;
        track('gah_gif_zone_view');
      });
    };
    window.addEventListener('scroll', onZoneScroll, { passive: true });
    onZoneScroll();
  }

  /* ---------- GAH-DIVOT-01: "See Every Strike", the seven divot types ----------
     The tile ships display:none with a src-less video until html.gd-divot lands
     (snippets/divot-flags, ?gd_divot=on|phone), so arming waits on the root
     class the way the GIF pair does, and reads the look then: the mat alone, or
     the mat beside a phone (html.gd-divot-phone), each with its own files.
     Poster and file are fetched when the tile comes within a screen of the
     viewport: phones take the small mp4, desktops the webm (the full mp4 where
     webm won't play). It plays only while on screen.
     Image mode (GAH-GIF-IAB-01): the Facebook and Instagram in-app browsers on
     iPhone refuse play(), so they get the mp4 as an <img> from the start, and
     any other iOS browser that refuses play() (Low Power Mode) switches over.
     Reduced motion keeps the poster: the finished pure strike. */
  const divotTile = $('.bm-pdp [data-gd-divot]');
  if (divotTile) {
    const root = document.documentElement;
    const video = $('video', divotTile);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const ua = navigator.userAgent;
    const iosWebKit = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const metaApp = iosWebKit && /FBAN|FBAV|FB_IAB|FBIOS|Instagram/.test(ua);
    let armed = false;
    let asImage = false;
    let files = null;

    const pickFiles = () => {
      const data = video.dataset;
      const phone = root.classList.contains('gd-divot-phone');
      const get = (name) => data[phone ? `gdDivotPhone${name}` : `gdDivot${name}`];
      if (phone) video.setAttribute('aria-label', data.gdDivotLabelPhone);
      return { poster: get('Poster'), webm: get('Webm'), desktop: get('Desktop'), mobile: get('Mobile') };
    };
    const showPoster = () => {
      if (!video.getAttribute('poster')) video.poster = files.poster;
    };
    const load = () => {
      if (asImage || video.getAttribute('src')) return;
      showPoster();
      video.preload = 'auto';
      if (window.innerWidth < 990) video.src = files.mobile;
      else video.src = video.canPlayType('video/webm; codecs="vp9"') ? files.webm : files.desktop;
    };
    /* the image sits over the video, whose poster shows until it paints; the
       video's own fetch is dropped so the file isn't downloaded twice */
    const toImage = () => {
      if (asImage && $('.b-divot-img', divotTile)) return;
      asImage = true;
      showPoster();
      video.pause();
      if (video.getAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
      const img = document.createElement('img');
      img.className = 'b-divot-img';
      img.alt = video.getAttribute('aria-label') || '';
      img.decoding = 'async';
      img.src = files.mobile;
      video.after(img);
    };
    function refused(error) {
      if (iosWebKit && error && error.name === 'NotAllowedError') toImage();
    }

    const arm = () => {
      if (armed || !root.classList.contains('gd-divot')) return;
      armed = true;
      files = pickFiles();
      if (reduced.matches || !('IntersectionObserver' in window)) {
        showPoster();
        return;
      }
      if (metaApp) asImage = true;
      const near = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        near.disconnect();
        if (asImage) toImage();
        else load();
      }, { rootMargin: '100% 0px' });
      near.observe(divotTile);
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (asImage) toImage();
            else {
              load();
              video.play().catch(refused);
            }
          } else if (!asImage) {
            video.pause();
          }
        });
      }).observe(divotTile);
    };
    new MutationObserver(arm).observe(root, { attributes: true, attributeFilter: ['class'] });
    arm();
  }

  /* ---------- GAH-MODES-02: five full-art mode cards in "Unique Ways to Play at Home" ----------
     Arm B's wrap ships display:none with src-less images. Intelligems lands
     2.5-5 s after paint, so nothing here is decided at init: a MutationObserver
     on the root class hands the art its srcset whenever the arm class lands
     (loading="lazy" still holds the download until the band is near).
     Events (all gated on modesFiveTest, all once per pageview, identical in both
     arms so the read is like for like):
       gah_modes5_zone_view  the band heading's bottom edge came on screen. Same
                            element in both arms: the arm-independent
                            denominator, because most sessions never get here.
       gah_modes5_band_view  the rendered rail was >= 50% on screen for 500 ms.
       gah_modes5_swipe      first sideways move of the rail (it only scrolls
                            below 990; desktop lays every card out at once).
       gah_modes5_depth      how many of the cards had their centre on screen,
                            sent when the band leaves the screen or the page
                            hides. */
  const modes5Band = $('.bm-pdp #play');
  const modes5Wrap = modes5Band && $('[data-gd-modes5]', modes5Band);
  if (modes5Band && modes5Wrap) {
    const root = document.documentElement;
    const flags = () => window.GD_FLAGS || {};
    const modes5Head = $('.section-head', modes5Band) || modes5Band;
    const wraps = $$('.rail-wrap', modes5Band);
    const seen = { zone: false, band: false, swiped: false, cards: 0, total: 0 };
    const seenCards = new Set();
    let depthSent = false;
    let viewTimer = 0;
    let lastArm = null;

    // Intelligems targets by URL path, and this section also renders on the
    // launch-bundle PDP: the template that can carry the arm is the only one
    // that may report on it (the exposure gate in theme.liquid, repeated)
    const onModesTemplate = () => !!(window._template && window._template.suffix === 'divot_daddy_pro_splittest');
    const track = (name, tags) => {
      if (!flags().modesFiveTest || !onModesTemplate()) return;
      const payload = Object.assign({ gah_modes5_arm: flags().modesFiveArm ? flags().modesFiveArm() : 'a_four_tiles' }, tags || {});
      if (flags().track) flags().track(name, payload);
    };
    const publish = () => {
      seen.cards = seenCards.size;
      window.GD_FLAGS = window.GD_FLAGS || {};
      window.GD_FLAGS.modesFiveSeen = seen;
    };

    // whichever wrap has a box is the arm that rendered
    const activeWrap = () => wraps.find((wrap) => wrap.getClientRects().length) || null;
    const onScreen = (rect) => {
      if (!rect.height) return 0;
      const shown = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      return Math.max(0, shown) / rect.height;
    };
    const countCards = () => {
      const wrap = activeWrap();
      const railEl = wrap && $('.rail', wrap);
      if (!railEl) return;
      const port = railEl.getBoundingClientRect();
      const cards = [...railEl.children];
      cards.forEach((card, i) => {
        const box = card.getBoundingClientRect();
        const centre = box.left + box.width / 2;
        if (centre >= Math.max(port.left, 0) && centre <= Math.min(port.right, window.innerWidth)) seenCards.add(i);
      });
      seen.total = cards.length;
      publish();
    };

    const loadModesArt = () => {
      const arm = root.classList.contains('gd-modes-five');
      // a late flip swaps the rail under the visitor: what was counted belongs
      // to cards that are no longer there
      if (lastArm !== null && lastArm !== arm && !depthSent) seenCards.clear();
      lastArm = arm;
      if (!arm) return;
      $$('img[data-gd-modes5-src]', modes5Wrap).forEach((img) => {
        if (img.getAttribute('src')) return;
        // srcset first, or the browser starts on the src fallback and then re-picks
        img.srcset = img.dataset.gdModes5Srcset;
        img.src = img.dataset.gdModes5Src;
      });
    };

    const sendDepth = () => {
      if (depthSent || !seen.band) return;
      depthSent = true;
      countCards();
      track('gah_modes5_depth', {
        gah_modes5_cards_seen: seen.cards,
        gah_modes5_cards_total: seen.total,
        gah_modes5_swiped: seen.swiped ? 1 : 0,
      });
    };

    const check = () => {
      // inert (no test running) costs no layout read
      if (!flags().modesFiveTest || depthSent) return;
      if (!seen.zone && modes5Head.getBoundingClientRect().bottom <= window.innerHeight) {
        seen.zone = true;
        publish();
        track('gah_modes5_zone_view');
      }
      const wrap = activeWrap();
      if (!wrap) return;
      const share = onScreen(wrap.getBoundingClientRect());
      if (!seen.band) {
        if (share >= 0.5 && !viewTimer) {
          viewTimer = window.setTimeout(() => {
            viewTimer = 0;
            const now = activeWrap();
            if (seen.band || !now || !flags().modesFiveTest || onScreen(now.getBoundingClientRect()) < 0.5) return;
            seen.band = true;
            countCards();
            track('gah_modes5_band_view');
          }, 500);
        } else if (share < 0.5 && viewTimer) {
          window.clearTimeout(viewTimer);
          viewTimer = 0;
        }
      } else if (share === 0) {
        sendDepth();
      }
    };

    new MutationObserver(() => {
      loadModesArt();
      check();
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
    loadModesArt();

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        check();
      });
    }, { passive: true });

    wraps.forEach((wrap) => {
      const railEl = $('.rail', wrap);
      if (!railEl) return;
      let railTicking = false;
      railEl.addEventListener('scroll', () => {
        if (railTicking || !flags().modesFiveTest || depthSent) return;
        railTicking = true;
        window.requestAnimationFrame(() => {
          railTicking = false;
          countCards();
          if (!seen.swiped && railEl.scrollLeft > 8) {
            seen.swiped = true;
            publish();
            track('gah_modes5_swipe');
          }
        });
      }, { passive: true });
    });

    // control flips no class, so nothing above wakes up when Intelligems turns
    // the test on under a visitor who is already parked on the band
    let polls = 0;
    const poll = window.setInterval(() => {
      polls += 1;
      if (flags().modesFiveTest || polls >= 20) {
        window.clearInterval(poll);
        check();
      }
    }, 500);
    window.addEventListener('pagehide', sendDepth);
  }

  const floors = $$('.bm-pdp [data-pdp-floor]');
  if (floors.length && !('IntersectionObserver' in window)) {
    floors.forEach((figure) => figure.classList.add('is-tracing'));
  } else if (floors.length) {
    const floorIo = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.target.classList.toggle('is-tracing', entry.isIntersecting)),
      { threshold: 0.35 }
    );
    floors.forEach((figure) => floorIo.observe(figure));
  }

  /* ---------- Hero video: autoplay on every platform ----------
     Paul (Aug 24) wants the Living Room clip playing everywhere, including the
     Facebook and Instagram in-app browsers that ad traffic lands in.

     What actually gates autoplay:
       - muted must be true as a PROPERTY before play(), not just an attribute.
         Some webviews parse the attribute late and reject an unmuted play().
       - playsinline keeps iOS from taking over the screen. This is the fix for
         the TikTok force-fullscreen behaviour that sank the July attempt.
       - a blocked play() throws NotAllowedError (Low Power Mode, Data Saver,
         strict webviews). We retry, then arm a one-shot listener so the very
         first tap or scroll anywhere on the page starts it. If none of that
         works the poster is already painted, so the slide still looks right.
     Pausing off-screen keeps it off the battery and stops webviews reclaiming
     the video for their own fullscreen player. */
  const heroVideo = $('.bm-pdp [data-pdp-hero-video]');
  if (heroVideo) {
    let started = false;
    let gestureArmed = false;

    const onScreen = () => {
      const r = heroVideo.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return !document.hidden && r.right > 1 && r.bottom > 1 && r.left < vw - 1 && r.top < vh - 1;
    };

    const armGesture = () => {
      if (gestureArmed) return;
      gestureArmed = true;
      const kick = () => {
        attempt();
        ['touchstart', 'pointerdown', 'click', 'scroll'].forEach((evt) =>
          window.removeEventListener(evt, kick, true)
        );
      };
      ['touchstart', 'pointerdown', 'click', 'scroll'].forEach((evt) =>
        window.addEventListener(evt, kick, { capture: true, passive: true })
      );
    };

    function attempt() {
      if (!onScreen()) return;
      heroVideo.muted = true;
      heroVideo.defaultMuted = true;
      const p = heroVideo.play();
      if (p && p.catch) {
        p.catch((err) => {
          if (!err || err.name !== 'NotAllowedError') return;
          /* GAH-GIF-IAB-01: iOS plays the clip as an image instead (the swap
             lives under the rail in benchmark-pdp-main.liquid) */
          const flags = window.GD_FLAGS || {};
          if (flags.heroIos && flags.heroToImage && flags.heroToImage()) return;
          armGesture();
        });
      }
    }

    heroVideo.addEventListener('playing', () => { started = true; });
    /* the theme and some webviews pause videos on their own; resume if we are
       still on screen and the user has not scrolled past */
    heroVideo.addEventListener('pause', () => {
      if (started && onScreen()) setTimeout(attempt, 0);
    });
    heroVideo.addEventListener('loadeddata', attempt);
    heroVideo.addEventListener('canplay', attempt);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { try { heroVideo.pause(); } catch (e) {} } else attempt();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) attempt();
          else if (!heroVideo.paused) { try { heroVideo.pause(); } catch (e) {} }
        });
      }, { threshold: [0, 0.15] }).observe(heroVideo);
    }
    attempt();
  }

  /* ---------- Lazy autoplay videos [data-bm-video-src] ---------- */
  const lazyVideos = $$('.bm-pdp video[data-bm-video-src]');
  if (lazyVideos.length) {
    const play = (video) => {
      video.src = video.dataset.bmVideoSrc;
      video.play().catch(() => {});
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              play(entry.target);
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '300px' }
      );
      lazyVideos.forEach((v) => io.observe(v));
    } else {
      lazyVideos.forEach(play);
    }
  }

  /* ---------- Bento: the Simulation Mat scan pass ----------
     The overlay loops forever, so it only runs while the tile is on screen -
     the same IntersectionObserver gate the how-it-works step overlays use
     (hiw-benchmark.js, threshold 0.35, toggled both ways). Without
     IntersectionObserver, or under reduced motion, the class still goes on and
     the css pins the settled frame rather than leaving the photo bare. */
  const matTiles = $$('.bm-pdp .b-mat');
  if (matTiles.length) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    matTiles.forEach((tile) => {
      if (!('IntersectionObserver' in window) || reducedMotion.matches) {
        tile.classList.add('is-scanning');
        return;
      }
      // the observer can batch several crossings into one callback; the last
      // entry is the current state
      new IntersectionObserver(
        (entries) => tile.classList.toggle('is-scanning', entries[entries.length - 1].isIntersecting),
        { threshold: 0.35 }
      ).observe(tile);
    });
  }

  /* ---------- Scroll parallax [data-plx] — layers translate at different rates.
     Runs regardless of prefers-reduced-motion (client explicitly wants it). ---------- */
  const plxEls = $$('.bm-pdp [data-plx]');
  if (plxEls.length) {
    let plxRaf = null;
    const plxUpdate = () => {
      plxRaf = null;
      plxEls.forEach((el) => {
        const scope = el.closest('[data-plx-scope]') || el.parentElement;
        const r = scope.getBoundingClientRect();
        if (r.bottom < -80 || r.top > innerHeight + 80) return;
        const progress = (innerHeight - r.top) / (innerHeight + r.height) - 0.5;
        const speed = parseFloat(el.dataset.plx) || 0.3;
        el.style.transform = `translateY(${(-progress * speed * 240).toFixed(1)}px)`;
      });
    };
    const plxQueue = () => {
      if (!plxRaf) plxRaf = requestAnimationFrame(plxUpdate);
    };
    window.addEventListener('scroll', plxQueue, { passive: true });
    window.addEventListener('resize', plxQueue, { passive: true });
    plxUpdate();
  }

  /* ---------- Sticky ATC bars toggle past the buy box ---------- */
  const stickyAtc = $('[data-sticky-atc]');
  const stickyBar = $('[data-sticky-bar]');
  const buybox = $('.bm-pdp [data-buybox]');

  if ((stickyAtc || stickyBar) && buybox && 'IntersectionObserver' in window) {
    // the bars used to be cleared with ~106px of footer padding, which is 64px
    // more than figma leaves under the copyright row. Retract them while that
    // row is on screen instead, so the footer keeps figma's 32px close.
    const footerEnd = $('.footer-benchmark__bottom') || $('.footer-benchmark');
    let pastBuybox = false;
    let atFooter = false;
    const apply = () => {
      const show = pastBuybox && !atFooter;
      stickyAtc?.classList.toggle('show', show);
      stickyBar?.classList.toggle('show', show);
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        pastBuybox = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        apply();
      },
      { threshold: 0 }
    );
    io.observe(buybox);

    if (footerEnd) {
      const footerIo = new IntersectionObserver(
        ([entry]) => {
          atFooter = entry.isIntersecting;
          apply();
        },
        { threshold: 0 }
      );
      footerIo.observe(footerEnd);
    }
  }

  /* ---------- Add to cart via Shopify AJAX API ---------- */
  const cartAddUrl = `${window.routes?.cart_add_url || '/cart/add'}.js`;
  const postItems = (items, sectionIds) => {
    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname });
    return fetch(cartAddUrl, config);
  };

  /* Both guards below need a FRESH cart, never window._cartState. The cache is
     only refreshed on this page's own writes, so a /cart/clear.js, another tab
     or a back-navigation leaves it stale, and a stale cache here does real
     damage: it once dropped a bundle's coaching while the hardware still
     posted ($158 charged, two of the three items delivered), and once dropped
     a FIRST add and left the button dead. addToCart reads the cart ONCE for
     both - from the attribute write's response when one is due, else a single
     GET - and only for an add that carries a plan or a bundle tag. The drawer
     has already drawn the predicted add by then; this stays the correctness
     gate. A missing cart (the read failed) passes every item through, as the
     old fetch-per-guard did. */
  const needsOwnedCheck = (items) =>
    items.some((item) => item.selling_plan || (item.properties && item.properties._bm_bundle));

  // a plan line is a membership, not a multi-pack: /cart/add.js increments the
  // matching line, and the cart renders plan lines with no stepper and a unit
  // price, so a second bundle click leaves an invisible extra charge in the
  // total. drop plan items the cart already holds and add the rest
  const dropOwnedSubscriptions = (items, cart) => {
    if (!cart || !items.some((item) => item.selling_plan)) return items;
    const owned = new Set(
      (cart.items || [])
        .filter((line) => line.selling_plan_allocation)
        .map((line) => `${line.variant_id}@${line.selling_plan_allocation.selling_plan.id}`)
    );
    /* Bundle lines are exempt. This guard stops a second click stacking a
       membership the shopper already holds, which is right for a standalone
       plan line. A bundle is ONE thing: dropping the coaching out of it bills
       a bundle price for part of a bundle. dropOwnedBundle already covers the
       repeat-click case for the whole group, all-or-nothing. */
    return items.filter(
      (item) =>
        !item.selling_plan ||
        (item.properties && item.properties._bm_bundle) ||
        !owned.has(`${item.id}@${item.selling_plan}`)
    );
  };

  /* GAH-SUB-01 arm B: the same guard, for the hardware the plan line travels
     with. Arm B posts the bundle as three real lines. /cart/add.js MERGES a line
     of the same variant, so a second click of the bundle button raised the mat
     and the pad to quantity 2 while the coaching line stayed at 1 -
     dropOwnedSubscriptions above protects only the plan line. A test cart
     reached 3 pads and read $243 instead of $158.

     A bundle is one thing to the shopper, so a repeat click on a bundle already
     in the cart adds nothing. Dropping every line of the group empties the list,
     and the caller already treats an empty list as "open the drawer, post
     nothing". Every data-bm-bundle button tags its lines, the single native
     bundle token included, so this covers arm A's repeat click too. */
  const dropOwnedBundle = (items, cart) => {
    const keys = new Set(
      items.map((item) => item.properties && item.properties._bm_bundle).filter(Boolean)
    );
    if (!cart || !keys.size) return items;
    const owned = new Set(
      (cart.items || []).map((line) => line.properties && line.properties._bm_bundle).filter(Boolean)
    );
    if (![...keys].some((key) => owned.has(key))) return items;
    return items.filter((item) => !(item.properties && owned.has(item.properties._bm_bundle)));
  };

  // the drawer markup is server-rendered with the cart as it was at page load, and
  // `cart-drawer:not(.is-empty) .drawer__inner-empty { display: none }` is the only
  // thing hiding the empty block. Opening the drawer before the POST resolves without
  // this left "Your cart is empty" on screen for the whole round trip. Same shape as
  // upsell-products.js showOptimisticCartUpdate(); renderContents() already strips
  // .cart-item--skeleton, so the success path needs no teardown.
  const ROWGROUP = '#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]';

  const skeletonRow = (preview) => {
    const row = document.createElement('div');
    row.setAttribute('role', 'row');
    row.className = 'cart-item cart-item--skeleton';
    row.innerHTML = `
      <div class="cart-item__media skeleton-pulse"></div>
      <div class="cart-item__content-wrapper">
        <div class="skeleton-content">
          <div class="skeleton-line skeleton-pulse" style="width:60%;height:14px"></div>
          <div class="skeleton-line skeleton-pulse" style="width:80%;height:20px;margin-top:10px"></div>
          <div class="skeleton-line skeleton-pulse" style="width:100px;height:34px;margin-top:12px;border-radius:60px"></div>
        </div>
      </div>`;
    // the button knows what it posts (data-bm-preview), so the in-flight row
    // is drawn FINISHED on the click frame - image at the same crops the
    // server row uses (the swap is a cache hit), title, inert stepper and
    // bin, price with struck compare. Nothing shimmers; renderContents then
    // swaps in the live row. No preview data keeps the legacy skeleton.
    if (preview && (preview.t || preview.i100)) {
      const media = row.querySelector('.cart-item__media');
      const content = row.querySelector('.skeleton-content');
      media.classList.remove('skeleton-pulse');
      media.style.background = '#f3f3f4';
      if (preview.i100) {
        media.innerHTML = `
          <picture>
            <source media="(max-width: 480px)" srcset="${preview.i80} 1x, ${preview.i160} 2x">
            <img class="cart-item__image" src="${preview.i100}" srcset="${preview.i100} 1x, ${preview.i200} 2x" alt="" width="100" height="100">
          </picture>`;
      }
      const line = (tag, css) => {
        const el = document.createElement(tag);
        el.style.cssText = css;
        return el;
      };
      content.innerHTML = '';
      content.style.gap = '12px';
      const head = line('div', 'display:flex;align-items:flex-start;justify-content:space-between;gap:12px');
      const name = line('div', 'font-size:16px;font-weight:600;line-height:1.3;padding-top:4px');
      // n is the drawer's own bundle-name markup (see the GAH-WINTER-01 note
      // where previews are parsed); every other row prints the plain title
      if (preview.n) name.innerHTML = preview.n;
      else name.textContent = preview.t || '';
      const bin = line('div', 'flex:none;width:44px;height:44px;border:1px solid #E5E5E5;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#121212');
      bin.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/></svg>';
      head.append(name, bin);
      const foot = line('div', 'display:flex;align-items:center;justify-content:space-between;gap:12px');
      const qty = line('div', 'display:flex;align-items:center;justify-content:space-between;width:126px;height:48px;padding:0 16px;border:1px solid #E5E5E5;border-radius:12px;font-size:16px;font-weight:600');
      qty.innerHTML = '<span aria-hidden="true">&minus;</span><span>1</span><span aria-hidden="true">+</span>';
      const price = line('div', 'display:flex;align-items:baseline;gap:8px;font-size:20px;font-weight:700');
      // GAH-CA-CAD: the amount sits in its own .bm-cur span (a flex item, same
      // layout as the bare text node) so benchmark-cur-mark can put "CAD"
      // after it and not after the struck compare
      const now = document.createElement('span');
      now.className = 'bm-cur';
      now.textContent = preview.p || '';
      price.append(now);
      if (preview.c) {
        const cmp = line('s', 'font-size:16px;font-weight:500;color:#9a9a9a');
        cmp.textContent = preview.c;
        price.append(cmp);
      }
      foot.append(qty, price);
      content.append(head, foot);
    }
    return row;
  };

  const showAddingState = (cartDrawer, count, previews = []) => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-empty');

    const drawerItems = cartDrawer.querySelector('cart-drawer-items');
    if (drawerItems) {
      drawerItems.classList.remove('is-empty');
      // clear the forced display:none _maybeHideItemsForEmpty leaves behind
      drawerItems.style.display = '';
    }
    const drawerInner = cartDrawer.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');

    const checkoutBtn = cartDrawer.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

    const container = cartDrawer.querySelector(ROWGROUP);
    if (!container) return;
    for (let i = 0; i < count; i++) container.prepend(skeletonRow(previews[i]));
  };

  const clearAddingState = (cartDrawer) => {
    if (!cartDrawer) return;
    cartDrawer.querySelectorAll('.cart-item--skeleton').forEach((el) => el.remove());
    if (window.CartOptimist) CartOptimist.revert();

    const hasRealItems = !!cartDrawer.querySelector(`${ROWGROUP} .cart-item:not(.cart-item--skeleton)`);
    if (!hasRealItems) cartDrawer.classList.add('is-empty');
  };

  /* ---------- split-test attribution: one cart write ----------
     Every stamp*Arm below decides, on the click, whether its test is running
     and which arm this visitor saw, then queues its keys here. addToCart sends
     the lot as ONE /cart/update.js (update.js merges attribute keys, so no
     test overwrites another's). They used to be fifteen awaited round trips in
     front of the add - six fired on a first Golf At Home add - and nothing on
     screen depended on them: no Liquid reads cart.attributes. They only have
     to be on the cart before checkout, and the checkout guard in
     cart-instant.js holds Check out until the write lands. */
  let queuedArmAttributes = null;
  const stampAttributes = (attributes) => {
    queuedArmAttributes = { ...(queuedArmAttributes || {}), ...attributes };
  };
  const takeArmAttributes = () => {
    const attributes = queuedArmAttributes;
    queuedArmAttributes = null;
    return attributes;
  };
  const cartUrl = window.routes?.cart_url || '/cart';
  /* resolves to the full cart Shopify returns, or null - attribution is best
     effort and never blocks the sale */
  const writeArmAttributes = async (attributes) => {
    try {
      const response = await fetch(`${cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  };
  /* The attribution write, checked once against a fresh read a moment later
     and repeated if it did not stick: Intelligems reads the cart around theme
     writes, and a write inside one of those reads can be acknowledged and then
     lost. Checkout waits for this (InstantCart.hold). */
  const writeArmAttributesVerified = async (attributes) => {
    const cart = await writeArmAttributes(attributes);
    let checked = null;
    const check = () => {
      if (!checked) {
        checked = (async () => {
          try {
            const fresh = await (await fetch(`${cartUrl}.js`, { cache: 'no-store' })).json();
            const have = (fresh && fresh.attributes) || {};
            const missing = Object.keys(attributes).some((k) => String(have[k] == null ? '' : have[k]) !== String(attributes[k]));
            if (missing) await writeArmAttributes(attributes);
          } catch (error) {
            /* best effort */
          }
        })();
      }
      return checked;
    };
    /* read back a moment later; a Check out tap before then runs the same
       read-back right before it leaves for checkout (cart-instant.js), so a
       fast buyer's order still carries its arms. Once it has run it leaves
       the checkout queue. */
    const unregister = window.InstantCart && window.InstantCart.beforeCheckout
      ? window.InstantCart.beforeCheckout(check)
      : () => {};
    setTimeout(() => { check().finally(unregister); }, 1500);
    return cart;
  };
  const readCart = async () => {
    try {
      const response = await fetch(`${cartUrl}.js`, { cache: 'no-store' });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  };

  /* GAH-BUNDLE-01 attribution. Stamps the arm onto the cart, which carries it
     through to the Shopify order - the only place the outcome can actually be
     measured, since Clarity fired a Purchase event once in three days.
     (The drawer's upgrade card itself is composition-gated, not arm-gated -
     it renders in both arms by design, so nothing on screen waits on this.)

     Tags BOTH arms. Tagging only arm A would leave arm B's orders
     indistinguishable from untested traffic. */
  let gahArmWritten = false;
  const stampGahArm = () => {
    if (gahArmWritten) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    // no running test means no extra round trip in front of Add to Cart
    if (!(window.GD_FLAGS && window.GD_FLAGS.bundleTest)) return;
    gahArmWritten = true;
    const arm = window.GD_FLAGS && window.GD_FLAGS.armGah;
    if (!arm) return;
    stampAttributes({ gah_arm: arm });
  };

  /* GAH-DELTA-01 attribution, same shape as stampGahArm but its own latch and
     attribute key (update.js merges keys, so the two coexist; sharing gah_arm
     would corrupt GAH-BUNDLE-01's readout). No template gate: bundle adds
     fire from the GAH PDP, the bundle PDP, and reels pages alike. */
  let deltaArmWritten = false;
  const stampDeltaArm = () => {
    if (deltaArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.deltaTest)) return;
    deltaArmWritten = true;
    const arm = document.documentElement.classList.contains('gd-delta-b') ? 'b_49' : 'a_39';
    stampAttributes({ gd_delta_arm: arm });
  };

  /* DEAL-5 — the $119 vs $149 mat price test. Arm B charges the hidden $149
     twin mat AND the hidden $188 twin bundle, so the "+$39 upgrade" delta reads
     identically in both arms and only the mat price varies.

     Deliberately namespaced away from GAH-DELTA-01 (GD_DELTA / gd-delta-b /
     deltaTest): the two tests price the same two surfaces and can be live at
     the same time, so they may never share a flag, a class, an id map or a cart
     attribute.

     The gate is the full three layers, re-read on every call because Intelligems
     assigns 2.5-5s after load: html.gd-deal-b is written by GD_FLAGS.apply()
     only when dealTest && dealPlus && GD_DEAL.bId && GD_DEAL.market === 'US',
     and the flags and the config are re-checked here as well. A missing or
     mispriced twin leaves bId at 0 and the whole test physically inert. */
  const dealArmB = () => {
    const flags = window.GD_FLAGS;
    const deal = window.GD_DEAL;
    return !!(
      flags &&
      flags.dealTest === true &&
      flags.dealPlus === true &&
      deal &&
      // Number(), not truthiness: a stamped "0" is a truthy string, and a
      // missing twin has to disarm the arm rather than half-arm it
      Number(deal.bId) > 0 &&
      // the bundle twin must exist too: a half-armed test quotes $158 and charges $188
      Number(deal.bundleBId) > 0 &&
      deal.market === 'US' &&
      document.documentElement.classList.contains('gd-deal-b') &&
      // PRECEDENCE: dealMax wins and dealPlus is ignored whenever tier C is
      // live. A no-op while dealMax is absent (gd-deal-c is never written), so
      // the arm-B path stays exactly what it is today.
      !document.documentElement.classList.contains('gd-deal-c')
    );
  };

  /* DEAL-5 TIER C — the $179 mat / $218 bundle tier, a third price over the
     $119 control and the $149 arm B. Deliberately a SECOND boolean rather than
     a refactor of dealPlus into an enum: dealPlus=true still means $149 and
     nothing about the already-verified arm-B path moves.

     PRECEDENCE, and it is the only interaction between the two flags: if both
     dealPlus and dealMax are somehow set, dealMax WINS and dealPlus is ignored
     — enforced here (this gate never reads dealPlus), in dealArmB above (which
     refuses while gd-deal-c is present), and in the swap below, which tests
     tier C first.

     Same three layers as tier B, re-read on every call because Intelligems
     assigns 2.5-5s after load, plus tier B's two extra locks: US-only, and it
     refuses to arm under GAH-DELTA-01's class. cId/bundleCId are 0 whenever a
     tier-C twin is missing or priced at or below its tier-B counterpart, which
     disarms tier C alone and leaves tiers A and B untouched. */
  const dealArmC = () => {
    const flags = window.GD_FLAGS;
    const deal = window.GD_DEAL;
    return !!(
      flags &&
      flags.dealTest === true &&
      flags.dealMax === true &&
      deal &&
      // Number(), not truthiness: a stamped "0" is a truthy string
      Number(deal.cId) > 0 &&
      // the bundle twin must exist too: a half-armed tier quotes $158 and charges $218
      Number(deal.bundleCId) > 0 &&
      deal.market === 'US' &&
      document.documentElement.classList.contains('gd-deal-c') &&
      // never co-arm with GAH-DELTA-01, matching tier B: both tests swap the
      // SAME control bundle id, so two swaps would fight over one add
      !document.documentElement.classList.contains('gd-delta-b')
    );
  };

  /* DEAL-5 attribution, the same shape as stampGahArm and stampDeltaArm but
     with its own latch and its own attribute key: /cart/update.js MERGES the
     attributes object, so gah_arm, gd_delta_arm and gd_deal_arm coexist on one
     cart, and reusing another test's key would corrupt its readout. No template
     gate - the mat and the bundle are added from the GAH PDP, the bundle PDP
     and the drawer alike. Tags BOTH arms: tagging only arm B would leave arm A
     indistinguishable from untested traffic. */
  let dealArmWritten = false;
  const stampDealArm = () => {
    if (dealArmWritten) return;
    // no running test means no extra round trip in front of Add to Cart
    if (!(window.GD_FLAGS && window.GD_FLAGS.dealTest)) return;
    dealArmWritten = true;
    // read at write time, never captured at init. Tier C is tested FIRST: the
    // documented precedence is dealMax over dealPlus, so a cart that somehow
    // carries both classes is attributed to the tier that actually charged.
    const dealRoot = document.documentElement.classList;
    const arm = dealRoot.contains('gd-deal-c') ? 'c' : dealRoot.contains('gd-deal-b') ? 'b' : 'a';
    stampAttributes({ gd_deal_arm: arm });
  };

  /* GAH-SUB-01 attribution, the same shape again with its own latch and key
     (gah_sub_arm). Reads the class that rendered, at write time. */
  let subArmWritten = false;
  const stampSubArm = () => {
    if (subArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.subTest)) return;
    subArmWritten = true;
    const arm = window.GD_FLAGS.subArmName ? window.GD_FLAGS.subArmName() : 'a_plain';
    stampAttributes({ gah_sub_arm: arm });
  };

  /* GAH-DROP-01 attribution, the same shape as stampDeltaArm: own latch, own
     attribute key (update.js MERGES the attributes object, so gah_arm,
     gd_delta_arm, gd_deal_arm and gd_drop_arm coexist on one cart and none
     corrupts another's readout), gated on the test actually running so live
     traffic pays no extra round trip in front of Add to Cart. Template-gated
     like stampGahArm: the roll has one surface, the GAH buy box, so an add
     from the bundle or pad PDP is never a DROP-01 order. Tags BOTH arms -
     tagging only arm B would leave arm A indistinguishable from untested
     traffic. The arm is read at click time from the class that RENDERED,
     never captured at init: Intelligems lands 2.5-5s after load and a
     captured value is the dead-ATC lesson (see noBundleArm below). */
  let dropArmWritten = false;
  const stampDropArm = () => {
    if (dropArmWritten) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.dropTest)) return;
    dropArmWritten = true;
    const arm = document.documentElement.classList.contains('gd-price-drop') ? 'b_drop' : 'a_static';
    stampAttributes({ gd_drop_arm: arm });
  };

  /* GAH-PCT-01 attribution, the same shape as stampDropArm: own latch, own
     attribute key (gd_pct_arm merges alongside the four above), gated on the
     test actually running, template-gated because every rewritten label is on
     the GAH PDP. Tags all FOUR arms, p60 included - control has to be
     distinguishable from untested traffic. The arm is read at click time from
     the data-gd-pct attribute apply() RENDERED (absent = p60), never from
     pctLabel and never captured at init: a visitor bucketed into p63 who
     failed the market gate was shown 60, and that is the label their order
     is attributed to. */
  let pctArmWritten = false;
  const stampPctArm = () => {
    if (pctArmWritten) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.pctTest)) return;
    pctArmWritten = true;
    const arm = `p${document.documentElement.dataset.gdPct || '60'}`;
    stampAttributes({ gd_pct_arm: arm });
  };

  /* GAH-COACH-01 attribution, the same shape as stampPctArm: own latch, own
     attribute key (gd_coach_arm merges alongside the others), gated on the
     test actually running so live traffic pays no extra round trip in front
     of Add to Cart, template-gated because the section lives on the GAH PDP
     only. Tags BOTH arms - tagging only arm B would leave arm A
     indistinguishable from untested traffic. The arm is read at click time
     from the class that RENDERED, never captured at init: Intelligems lands
     2.5-5s after load and a captured value is the dead-ATC lesson. */
  let coachArmWritten = false;
  const stampCoachArm = () => {
    if (coachArmWritten) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.coachReviewTest)) return;
    coachArmWritten = true;
    // the class AND that placement's copy in the DOM: the arm is what
    // rendered, never the flag. apply() guarantees at most one class.
    const coachRoot = document.documentElement.classList;
    let arm = 'a_control';
    if (coachRoot.contains('gd-coach-top') && document.querySelector('.coach-review--top')) arm = 'c_top';
    else if (coachRoot.contains('gd-coach-review') && document.querySelector('.coach-review--reviews')) arm = 'b_reviews';
    stampAttributes({ gd_coach_arm: arm });
  };

  /* GAH-NAV-01 attribution, the same shape as stampDropArm: own latch, own
     attribute key (gd_nav_arm merges alongside the five above), gated on the
     test running. No template gate, like stampDeltaArm: the Shop menu is
     sitewide and this addToCart serves the GAH PDP and the bundle PDP alike,
     so an order from either carries the arm (the drawer upgrade stamps it
     too, in cart-drawer.liquid). Tags BOTH arms - control has to be
     distinguishable from untested traffic. Reads the class that RENDERED at
     this moment, never captured at init. */
  let navArmWritten = false;
  const stampNavArm = () => {
    if (navArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.navTest)) return;
    navArmWritten = true;
    const arm = document.documentElement.classList.contains('gd-nav-no-bundle') ? 'b_no_bundle' : 'a_bundle';
    stampAttributes({ gd_nav_arm: arm });
  };

  /* GAH-PHOTO-01 attribution, the same shape as stampNavArm: own latch, own
     attribute key (gd_photo_arm merges alongside the six above - update.js
     MERGES the attributes object, so none corrupts another's readout), gated
     on the test running so live traffic pays no extra round trip in front of
     Add to Cart. No template gate, like stampNavArm: photoTest is only ever
     set on the GAH PDP (Intelligems page-targets it) and the class is
     onGahPdp-scoped, so off-template the arm reads a_control anyway. Tags
     BOTH arms - control has to be distinguishable from untested traffic.
     Reads the class that RENDERED at this moment, never captured at init:
     Intelligems lands 2.5-5s after load and a captured value is the dead-ATC
     lesson. */
  let photoArmWritten = false;
  const stampPhotoArm = () => {
    if (photoArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.photoTest)) return;
    photoArmWritten = true;
    const arm = document.documentElement.classList.contains('gd-party-photo') ? 'b_party' : 'a_control';
    stampAttributes({ gd_photo_arm: arm });
  };

  /* GAH-PROMO-01 attribution, the same shape as stampNavArm: own latch, own
     attribute keys (gd_promo_arm + gd_promo_channel merge alongside the seven
     above - update.js MERGES the attributes object, so none corrupts
     another's readout), gated on the test running so live traffic pays no
     extra round trip in front of Add to Cart. No template gate, like
     stampNavArm: the bar is sitewide and this addToCart serves the GAH PDP
     and the bundle PDP alike (the drawer upgrade stamps it too, in
     benchmark-bundle-cart.js). Tags BOTH arms, and the channel in both, so
     an Instagram order that saw Labor Day is distinguishable from one that
     saw the Instagram name AND from untested traffic. Reads the class that
     RENDERED at this moment, never captured at init: Intelligems lands 2.5-5s
     after load and a captured value is the dead-ATC lesson. */
  let promoArmWritten = false;
  const stampPromoArm = () => {
    if (promoArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.promoInTest && window.GD_FLAGS.promoInTest())) return;
    promoArmWritten = true;
    const promoRoot = document.documentElement;
    const arm = promoRoot.classList.contains('gd-promo-channel') ? 'b_channel' : 'a_labor';
    const channel = promoRoot.dataset.gdChannel || 'none';
    stampAttributes({ gd_promo_arm: arm, gd_promo_channel: channel });
  };

  /* GAH-FALL-01 attribution, the stampNavArm shape: own latch, own key
     (gd_fall_arm), gated on the test running so live traffic pays nothing in
     front of Add to Cart, no template gate (the bar is sitewide). Tags ALL
     FOUR arms - arm A has to be distinguishable from untested traffic. Reads
     the arm that RENDERED, at click time. */
  let fallArmWritten = false;
  const stampFallArm = () => {
    if (fallArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.fallTest === true && window.GD_FLAGS.fallArmName)) return;
    fallArmWritten = true;
    stampAttributes({ gd_fall_arm: window.GD_FLAGS.fallArmName() });
  };

  /* GAH-SPX-01 attribution, the stampFallArm shape: own latch, own key
     (gd_spx_arm), gated on the test running, no template gate (the drawer
     total is sitewide). Tags BOTH arms; reads the arm that RENDERED
     (html.gd-spx) at click time. */
  let spxArmWritten = false;
  const stampSpxArm = () => {
    if (spxArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.spExclTest === true && window.GD_FLAGS.spxArmName)) return;
    spxArmWritten = true;
    stampAttributes({ gd_spx_arm: window.GD_FLAGS.spxArmName() });
  };

  /* GAH-TRIAL-01 attribution, the stampFallArm shape: own latch, own key
     (gd_trial_arm), gated on the visitor actually being in the test (running
     AND shown the subscription), reads the arm that RENDERED at click time. */
  let trialArmWritten = false;
  const stampTrialArm = () => {
    if (trialArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.trialInTest && window.GD_FLAGS.trialInTest())) return;
    trialArmWritten = true;
    stampAttributes({ gd_trial_arm: window.GD_FLAGS.trialArmName() });
  };

  /* GAH-PHONE-01 attribution, the stampPhotoArm shape: own latch, own keys,
     gated on the test running, tags all three arms, reads the classes that
     RENDERED at this moment. gd_phone_seen says whether this visitor had the
     bento zone / the tile on screen before adding to cart - the B-vs-C read is
     add-to-cart among visitors who reached the zone. One update.js call. */
  let phoneArmWritten = false;
  const stampPhoneArm = () => {
    if (phoneArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.phoneTest && window.GD_FLAGS.phoneArm)) return;
    phoneArmWritten = true;
    const seen = window.GD_FLAGS.phoneSeen || {};
    const arm = window.GD_FLAGS.phoneArm();
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gah_phone_atc',
        gah_phone_arm: arm,
        gah_phone_zone_seen: seen.zone ? 1 : 0,
        gah_phone_tile_seen: seen.tile ? 1 : 0,
      });
    } catch (error) {
      /* analytics is best effort */
    }
    stampAttributes({
      gd_phone_arm: arm,
      gd_phone_seen: seen.tile ? 'tile' : seen.zone ? 'zone' : 'none',
    });
  };

  /* GAH-PEEK-01 attribution, the stampPhoneArm shape: own latch, own keys,
     gated on the test running, tags all four arms, reads the classes that
     RENDERED at this moment. gd_peek_seen says whether this visitor opened the
     box before adding to cart and how many cards deep they went. One
     update.js call. */
  let peekArmWritten = false;
  const stampPeekArm = () => {
    if (peekArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.peekTest && window.GD_FLAGS.peekArm)) return;
    peekArmWritten = true;
    const seen = window.GD_FLAGS.peekSeen || {};
    const arm = window.GD_FLAGS.peekArm();
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gah_peek_atc',
        gah_peek_arm: arm,
        gah_peek_opened: seen.opened ? 1 : 0,
        gah_peek_depth: seen.depth || 0,
      });
    } catch (error) {
      /* analytics is best effort */
    }
    stampAttributes({
      gd_peek_arm: arm,
      gd_peek_seen: seen.opened ? `open_${seen.depth || 1}` : 'none',
    });
  };

  /* GAH-COURSES-01 attribution, the stampGifArm shape: own latch, own keys,
     gated on the test running, tags both arms, reads the arm that RENDERED
     (html[data-gd-courses], through GD_FLAGS.coursesArm). gd_courses_seen says
     whether this visitor had the bento tile on screen before adding to cart.
     One update.js call. */
  let coursesArmWritten = false;
  const stampCoursesArm = () => {
    if (coursesArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.coursesTest && window.GD_FLAGS.coursesArm)) return;
    // template-gated like the exposure: Intelligems targets the URL path, so
    // an alternate template on /products/golf-at-home (?view=...) gets
    // coursesTest true with neither node on the page
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    coursesArmWritten = true;
    const seen = window.GD_FLAGS.coursesSeen || {};
    const arm = window.GD_FLAGS.coursesArm();
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'gah_courses_atc', gah_courses_arm: arm, gah_courses_tile_seen: seen.tile ? 1 : 0 });
    } catch (error) {
      /* analytics is best effort */
    }
    stampAttributes({ gd_courses_arm: arm, gd_courses_seen: seen.tile ? 'tile' : 'none' });
  };

  /* GAH-GIF-01 attribution, the stampModesFiveArm shape: own latch, own keys,
     gated on the test running, tags both arms, reads the class that RENDERED.
     One update.js call. */
  let gifArmWritten = false;
  const stampGifArm = () => {
    if (gifArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.gifTest && window.GD_FLAGS.gifArm)) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    gifArmWritten = true;
    const seen = window.GD_FLAGS.gifSeen || {};
    const arm = window.GD_FLAGS.gifArm();
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'gah_gif_atc', gah_gif_arm: arm, gah_gif_zone_seen: seen.zone ? 1 : 0 });
    } catch (error) {
      /* analytics is best effort */
    }
    stampAttributes({ gd_gif_arm: arm, gd_gif_seen: seen.zone ? 'zone' : 'none' });
  };

  /* GAH-MODES-02 attribution, the stampPhoneArm shape: own latch, own keys,
     gated on the test running, tags both arms, reads the class that RENDERED
     at this moment. gd_modes5_seen says how far this visitor got into the band
     before adding to cart - the read is add-to-cart among visitors who
     reached it, because most sessions never scroll that far. One update.js
     call. */
  let modesFiveArmWritten = false;
  const stampModesFiveArm = () => {
    if (modesFiveArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.modesFiveTest && window.GD_FLAGS.modesFiveArm)) return;
    // template-gated like the exposure: Intelligems targets the URL path, so
    // an alternate template on /products/golf-at-home (?view=...) gets
    // modesFiveTest true with no band on the page, and would tag a cart
    // a_four_tiles for a visitor who was shown neither arm
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    modesFiveArmWritten = true;
    const seen = window.GD_FLAGS.modesFiveSeen || {};
    const arm = window.GD_FLAGS.modesFiveArm();
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gah_modes5_atc',
        gah_modes5_arm: arm,
        gah_modes5_zone_seen: seen.zone ? 1 : 0,
        gah_modes5_band_seen: seen.band ? 1 : 0,
        gah_modes5_swiped: seen.swiped ? 1 : 0,
        gah_modes5_cards_seen: seen.cards || 0,
      });
    } catch (error) {
      /* analytics is best effort */
    }
    stampAttributes({
      gd_modes5_arm: arm,
      gd_modes5_seen: seen.swiped ? 'swiped' : seen.band ? 'band' : seen.zone ? 'zone' : 'none',
    });
  };

  /* GAH-UPSELL-01 attribution, the stampModesFiveArm shape: own latch, own key
     (gd_upsell_arm), gated on the test running and template-gated like the
     exposure, tags both arms, reads the class that RENDERED at click time. In
     arm A this runs on the modal's Upgrade / No thanks / dismiss add, in arm B
     on the buy box add itself - either way before checkout. */
  let upsellArmWritten = false;
  const stampUpsellArm = () => {
    if (upsellArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.upsellTest && window.GD_FLAGS.upsellArm)) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    upsellArmWritten = true;
    stampAttributes({ gd_upsell_arm: window.GD_FLAGS.upsellArm() });
  };

  /* GAH-PZ-01 attribution, the stampUpsellArm shape: own latch, own keys,
     gated on the test running, template-gated like the exposure, tags all four
     arms with the arm that RENDERED (late_<arm> when Intelligems arrived after
     the page was shown). gd_pz_pick is the visitor's answer at Add to Cart:
     fun | improve | gift, or none. */
  let pzArmWritten = false;
  const stampPzArm = () => {
    if (pzArmWritten) return;
    if (!(window.GD_FLAGS && window.GD_FLAGS.pzTest && window.GD_FLAGS.pzArmName)) return;
    if (!(window._template && window._template.suffix === 'divot_daddy_pro_splittest')) return;
    pzArmWritten = true;
    const picked = document.querySelector('.bm-pdp > input.pz-radio:checked');
    stampAttributes({ gd_pz_arm: window.GD_FLAGS.pzArmName(), gd_pz_pick: picked ? picked.value : 'none' });
  };

  /* GAH-GIFTPASS-01 attribution, the stampPzArm shape: own latch, own key,
     only for a gift buyer in the running test (the exposure's population),
     on any add - a gift buyer who takes the simulator alone still counts.
     Tags the arm that RENDERED: a_subscription | b_prepaid. */
  let giftPassArmWritten = false;
  const stampGiftPassArm = () => {
    if (giftPassArmWritten) return;
    const flags = window.GD_FLAGS;
    if (!(flags && flags.giftPassInTest && flags.giftPassInTest() && flags.giftPicked())) return;
    giftPassArmWritten = true;
    stampAttributes({ gd_giftpass_arm: flags.giftPassArmName() });
  };

  const addToCart = async (button, ids) => {
    if (window._CART_ADD_IN_PROGRESS) return;
    window._CART_ADD_IN_PROGRESS = true;
    button.disabled = true;

    /* GAH-SUB-01 arm B/C: replace the single bundle token with the three real
       lines it contains, and hang the coaching selling plan on the third.

       A native bundle cannot carry a subscription - a component is a variant
       reference with no selling-plan field, so the bundle line always comes
       back with selling_plan_allocation: null even though the coaching product
       is set to subscription-only. A real cart line is the only thing checkout
       will render subscription terms for. Verified on the live store: these
       three lines total $158 and check out as "First payment $0.00, then
       $119.00 every year".

       Runs BEFORE the DELTA and DEAL maps below on purpose. Those key on the
       control mat id, and the expansion puts that id on the first line, so a
       price arm still swaps the mat exactly as it does for a single-token add.

       Fails open to arm A: any missing id leaves the bundle token untouched.
       The half-armed case is the dangerous one - posting the hardware without
       the coaching would charge $158 for two thirds of the bundle - so the
       config arms all-or-nothing rather than per-id. */
    const gsub = window.GD_SUB;
    const postsBundle = !!(gsub && ids.length === 1 && Number(ids[0].id) === Number(gsub.bundleId));
    // a fresh bundle, in any arm, clears an opt-out recorded against an earlier
    // one - queued with the arm stamps, so it rides their single write instead
    // of racing it as a second one
    if (postsBundle && window.GD_FLAGS && window.GD_FLAGS.subTest) {
      stampAttributes({ gah_sub_coaching: '' });
    }
    if (
      postsBundle && gsub.armed &&
      document.documentElement.classList.contains('gd-coach-sub')
    ) {
      // GAH-GIFT-SEAT-01: one card either way; the coaching line says who it is for
      const gsKey = window.GD_FLAGS && window.GD_FLAGS.bundleKey ? window.GD_FLAGS.bundleKey() : 'launch';
      /* GAH-GIFTPASS-01: the coaching slot - the $0 Gift Pass with no plan for
         a gift buyer in arm B, else Benchmark Unlimited on the plan of the
         trial arm that RENDERED (GAH-TRIAL-01, 7 or 30 days). Read now, at
         click time, from the class the visitor is looking at. */
      const coach = window.GD_FLAGS && window.GD_FLAGS.coachLine
        ? window.GD_FLAGS.coachLine()
        : { id: gsub.coachId, selling_plan: gsub.planId };
      const coachItem = {
        id: coach.id,
        properties: {
          _bm_bundle: gsKey,
          _bm_bundle_role: 'item',
          _bm_bundle_parent: String(gsub.matId),
          _bm_bundle_order: '2',
        },
      };
      // GAH-GIFT-SEAT-01: who the coaching is for, kept on the order line
      if (coach.seat) coachItem.properties._gd_seat = coach.seat;
      if (coach.selling_plan) coachItem.selling_plan = coach.selling_plan;
      ids = [
        { id: gsub.matId, properties: { _bm_bundle: gsKey, _bm_bundle_role: 'main' } },
        {
          id: gsub.padId,
          properties: {
            _bm_bundle: gsKey,
            _bm_bundle_role: 'item',
            _bm_bundle_parent: String(gsub.matId),
            _bm_bundle_order: '1',
          },
        },
        coachItem,
      ];
    }

    /* GAH-DELTA-01: arm B posts the $168 variant so checkout charges what the
       page showed. Keyed on the variant id, not data-bm-bundle - product-cta
       bundle buttons ship without that attr since the bundle became one line.
       The arm class is read here, at click time, never captured at init (the
       dead-ATC lesson). Fresh objects: the bound ids array must survive. */
    let deltaSwapped = false;
    const gdd = window.GD_DELTA;
    if (
      gdd && gdd.bId && gdd.aId &&
      document.documentElement.classList.contains('gd-delta-b')
    ) {
      ids = ids.map((item) => {
        if (Number(item.id) !== gdd.aId) return item;
        deltaSwapped = true;
        return { ...item, id: gdd.bId };
      });
    }

    /* DEAL-5: arm B posts the $149 mat and the $188 bundle so checkout charges
       what the page showed. Keyed on the VARIANT ID, never on a data attribute
       - several buy buttons ship without data-bm-bundle or data-bm-preview -
       and mapped over EVERY posted line, so a composite token add (mat + pad +
       coaching plan, or the bundle's own token list) swaps as a unit rather
       than only in the single-id case. The arm is read here, at click time,
       never captured at init (the dead-ATC lesson). Fresh objects throughout:
       the bound ids array is reused on the next click and must survive.

       Tier C ($179 mat / $218 bundle) rides the same map one tier up: aId ->
       cId and bundleAId -> bundleCId. It is chosen first, and the two maps are
       mutually exclusive, so the control ids are the only swap source in every
       tier and no line can be remapped twice. */
    let dealSwappedMat = false;
    let dealSwappedBundle = false;
    const deal = window.GD_DEAL;
    // both sides normalised through Number(): the config is stamped by another
    // snippet, and a quoted id would otherwise match nothing and silently leave
    // arm B showing $149 while charging $119
    const dealAId = deal ? Number(deal.aId) || 0 : 0;
    const dealBId = deal ? Number(deal.bId) || 0 : 0;
    const dealBundleAId = deal ? Number(deal.bundleAId) || 0 : 0;
    const dealBundleBId = deal ? Number(deal.bundleBId) || 0 : 0;
    const dealCId = deal ? Number(deal.cId) || 0 : 0;
    const dealBundleCId = deal ? Number(deal.bundleCId) || 0 : 0;
    /* Which tier this click charges, resolved ONCE here and then used for both
       the id swap and the preview substitution below. Tier C is tested first —
       dealMax wins over dealPlus — and exactly one of the two maps is ever
       applied, so an id can never be swapped twice. */
    const dealArmedC = dealArmC();
    const dealArmedB = !dealArmedC && dealArmB();
    if (dealArmedC || dealArmedB) {
      const dealMatId = dealArmedC ? dealCId : dealBId;
      const dealBundleId = dealArmedC ? dealBundleCId : dealBundleBId;
      ids = ids.map((item) => {
        const itemId = Number(item.id);
        if (dealAId && itemId === dealAId) {
          dealSwappedMat = true;
          return { ...item, id: dealMatId };
        }
        if (dealBundleAId && dealBundleId && itemId === dealBundleAId) {
          dealSwappedBundle = true;
          return { ...item, id: dealBundleId };
        }
        return item;
      });
      /* the bundle children carry _bm_bundle_parent, stamped from the CONTROL
         parent id when this button was bound. Re-point it at whatever the
         first line now posts, or the drawer would fail to nest the card in arm
         B. Cloned, never mutated: the properties objects hang off the bound
         ids array. A no-op in arm A, which never reaches this block. */
      const dealParentId = ids.length ? Number(ids[0].id) : 0;
      if (dealParentId) {
        ids = ids.map((item) =>
          item.properties && item.properties._bm_bundle_parent
            ? { ...item, properties: { ...item.properties, _bm_bundle_parent: String(dealParentId) } }
            : item
        );
      }
    }

    const cartDrawer = document.querySelector('cart-drawer');
    // open on the click frame — this page has no optimistic cart UI, so without this the
    // button sits disabled for the whole round trip with nothing on screen. The lines land
    // when renderContents runs below; the later open() calls become no-ops.
    let previews = [];
    try {
      previews = JSON.parse(button.dataset.bmPreview || '[]');
      if (!Array.isArray(previews)) previews = [];
    } catch (error) {
      previews = [];
    }
    // GAH-WINTER-01: a bundle button's preview title is the bundle product's
    // admin title ("The Benchmark Golf Fall Bundle"). InstantCart predicts the
    // buyer-seat add and draws the arm's name itself, but it has no price for
    // the prepaid gift seat, so that add falls back to this row - which then
    // said Fall under a Winter bar. The drawer carries the server row's name
    // spans in a <template>; the sale-arm CSS picks the visible one. Keyed on
    // what is posted too: the bundle page's product CTA posts the bundle token
    // without data-bm-bundle (adding it would turn on bundle line tagging).
    const bundleNameTpl = (button.dataset.bmBundle || postsBundle) ? document.querySelector('template[data-bm-bundle-name]') : null;
    if (bundleNameTpl && previews[0]) previews[0] = { ...previews[0], n: bundleNameTpl.innerHTML.trim() };
    // GAH-DELTA-01: the preview attr embeds the admin (control) price; when
    // the add was swapped to the B variant the click-frame row and optimistic
    // total must show what checkout will charge, not a $158 flash
    if (deltaSwapped) {
      previews = previews.map((p) => (p.pc === gdd.aCents ? { ...p, pc: gdd.bCents, p: gdd.bMoney } : p));
    }
    /* DEAL-5: same reason. data-bm-preview embeds the admin (control) price, so
       a swapped add would flash $119/$158 on the click frame and in the
       optimistic total before the server answered with $149/$188. Matched by
       cents rather than by index, so a button whose previews do not line up 1:1
       with its tokens is left alone, and skipped entirely when the config
       carries no cents for that surface - a missing preview number is a
       cosmetic flash, never a wrong charge. */
    if (dealSwappedMat) {
      const aCents = Number(deal.aCents) || 0;
      // whichever tier actually swapped the id above, so the click frame shows
      // the price this add will really be charged at
      const tierCents = dealArmedC ? Number(deal.cCents) || 0 : Number(deal.bCents) || 0;
      const tierMoney = dealArmedC ? deal.cMoney : deal.bMoney;
      if (aCents && tierCents) {
        previews = previews.map((p) => (p.pc === aCents ? { ...p, pc: tierCents, p: tierMoney || p.p } : p));
      }
    }
    if (dealSwappedBundle) {
      const aCents = Number(deal.bundleACents) || 0;
      const tierCents = dealArmedC
        ? Number(deal.bundleCCents) || 0
        : Number(deal.bundleBCents) || 0;
      const tierMoney = dealArmedC ? deal.bundleCMoney : deal.bundleBMoney;
      if (aCents && tierCents) {
        previews = previews.map((p) =>
          p.pc === aCents ? { ...p, pc: tierCents, p: tierMoney || p.p } : p
        );
      }
    }
    /* Split-test attribution, decided on the click. Each stamp reads its arm
       at THIS moment, exactly as before, and queues its keys; they go out
       below as one cart write. */
    stampGahArm();
    // GAH-SUPERINT-01 (assets/gah-superint.js): its arm key, or null by default
    const superintAttrs = window.GD_FLAGS?.superintArmAttrs ? window.GD_FLAGS.superintArmAttrs() : null;
    if (superintAttrs) stampAttributes(superintAttrs);
    // GAH-DARK-01 (snippets/dark-flags.liquid): its arm key, or null by default
    const darkAttrs = window.GD_FLAGS?.darkArmAttrs ? window.GD_FLAGS.darkArmAttrs() : null;
    if (darkAttrs) stampAttributes(darkAttrs);
    // GAH-GIFTDEF-01 (assets/gah-giftdef.js): its arm and page-switch count, or null by default
    const giftDefAttrs = window.GD_FLAGS?.giftDefArmAttrs ? window.GD_FLAGS.giftDefArmAttrs() : null;
    if (giftDefAttrs) stampAttributes(giftDefAttrs);
    // GAH-DIVOT-01 (assets/gah-divot.js): its arm, or null outside the test
    const divotAttrs = window.GD_FLAGS?.divotArmAttrs ? window.GD_FLAGS.divotArmAttrs() : null;
    if (divotAttrs) stampAttributes(divotAttrs);
    // OCT6 pdp lane (GAH-BIGPRICE-01 / GAH-BURGER-01, assets/oct6-pdp.js): its
    // arm keys, or null when neither test applies, so default adds queue nothing
    const oct6PdpAttrs = window.GD_FLAGS?.pdpLaneArmAttrs ? window.GD_FLAGS.pdpLaneArmAttrs() : null;
    if (oct6PdpAttrs) stampAttributes(oct6PdpAttrs);
    stampDeltaArm();
    stampDealArm();
    stampDropArm();
    stampPctArm();
    stampCoachArm();
    stampNavArm();
    stampPhotoArm();
    stampPromoArm();
    stampFallArm();
    stampSpxArm();
    stampTrialArm();
    stampPhoneArm();
    stampModesFiveArm();
    stampGifArm();
    stampPeekArm();
    stampCoursesArm();
    stampSubArm();
    stampUpsellArm();
    stampPzArm();
    stampGiftPassArm();
    const attributes = takeArmAttributes();

    const posted = ids.map((item) => ({ ...item, quantity: 1 }));

    /* The click frame. InstantCart predicts the cart this add produces and
       draws it finished - the rows the server will render (from Liquid
       prototypes), the upgrade card, Total with shipping protection, the SAVE
       pill and the header count - so the drawer opens already right. Without
       its prototypes (the first second of a page, or a variant it does not
       know) the old preview rows and summed total take over. */
    const instant = window.InstantCart ? window.InstantCart.begin(posted, { drawer: cartDrawer }) : null;
    if (!instant) {
      showAddingState(cartDrawer, ids.length, previews);
      // the drawer total otherwise reads $0 until the server answers; one summed
      // call because optimisticAdd re-bases from _cartState on every invocation
      const previewCents = previews.reduce((sum, p) => sum + (p.pc || 0), 0);
      if (previewCents && window.CartOptimist) {
        CartOptimist.optimisticAdd(previewCents, previews.reduce((sum, p) => sum + (p.cc || 0), 0), 0);
      }
    }
    cartDrawer?.open?.(button);
    let adding = true;

    /* Checkout waits for this add: the rows on screen are a prediction until
       the server has the lines, and the drawer form posts updates[] by line
       order (cart-instant.js holds the tap and replays it). */
    let releaseCheckout = () => {};
    let addOk = true;
    if (window.InstantCart) window.InstantCart.hold(new Promise((resolve) => { releaseCheckout = resolve; }));

    /* The network, shortest safe order:
       - an add carrying a plan or a bundle tag needs a fresh cart for the
         owned-line guards; the attribute write returns one, so it goes first
         and doubles as the read (else one GET);
       - any other add posts straight away and the attributes follow it.
       One cart write at a time either way. */
    let attributesWritten = !attributes;
    let attributesCarried = false;
    let fresh = null;
    if (needsOwnedCheck(posted)) {
      if (attributes) {
        fresh = await writeArmAttributes(attributes);
        attributesWritten = true;
      }
      if (!fresh) fresh = await readCart();
    }
    const items = dropOwnedBundle(dropOwnedSubscriptions(posted, fresh), fresh);
    const sectionIds = window.InstantCart
      ? window.InstantCart.sections()
      : cartDrawer?.getSectionsToRender?.().map((s) => s.section || s.id) || [];

    try {
      // every line was already in the cart - open the drawer rather than post
      // an empty payload, which /cart/add.js rejects
      if (!items.length) {
        if (instant) window.InstantCart.cancel(instant);
        clearAddingState(cartDrawer);
        adding = false;
        cartDrawer?.open?.(button);
        if (fresh && window.InstantCart) window.InstantCart.settle(null, fresh, false);
        return true;
      }

      // the skeleton count is per requested id; dropOwnedSubscriptions may have
      // removed some, so trim the extras rather than leave them shimmering
      if (!instant && items.length < ids.length && cartDrawer) {
        cartDrawer
          .querySelectorAll('.cart-item--skeleton')
          .forEach((el, i) => { if (i >= items.length) el.remove(); });
      }

      /* Shipping protection rides along with a first add (the cart has none
         yet), in the same request: posted after the products, it lands on top
         exactly as the old follow-up add left it, so line order - and with it
         the drawer form's updates[] - is unchanged. A tier change on a cart
         that already has protection stays with ShippingProtection.syncCart. */
      // the fresh read, when there was one, outranks the cache the ride was planned on
      const freshHasSp = !!(fresh && (fresh.items || []).some((line) => (line.handle || '').includes('shipping-protection')));
      let payload = instant && instant.spRide && !freshHasSp && items.length === posted.length ? [...items, instant.spRide] : items;
      const seqAtPost = window._CART_OPTIMISTIC_SEQ;
      let response = await postItems(payload, sectionIds);

      // a rejected protection line must not cost the add: retry the products
      // alone (ShippingProtection adds it afterwards) before anything below
      // decides the products themselves were the problem
      if (response.status === 422 && payload.length > items.length) {
        payload = items;
        response = await postItems(payload, sectionIds);
      }

      // a companion item can be unpurchasable (e.g. subscription-only) — fall
      // back to the main item alone, but say so instead of pretending. 422 is the
      // status Shopify uses for a bad line; retrying on anything else (a 429 in
      // particular) just fires a second request into the block that caused it.
      if (response.status === 422 && payload.length > 1) {
        // drop the bundle tagging with the companions: a lone line still
        // carrying _bm_bundle_role would draw the full bundle card in the
        // drawer with nothing inside it
        const { properties, ...mainLine } = items[0];
        payload = [mainLine];
        response = await postItems(payload, sectionIds);
        if (response.ok) toast('Added the simulator. The rest of the bundle could not be added.');
      }

      // a challenged or throttled cart endpoint clears on its own — one retry of
      // whatever payload was rejected, never a loop
      if (response.status === 429) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        response = await postItems(payload, sectionIds);
      }

      if (!response.ok) throw new Error(`Cart API error: ${response.status}`);

      const data = await response.json();
      // the whole post-add cart, rendered by sections/cart-instant-state in the
      // same response - no follow-up GET /cart.js to settle count and totals
      /* a drawer change made while this add was in flight (a stepper tap) can
         answer first; this snapshot then predates it, so it is not handed to
         the protection sync or the cached state - both re-read instead */
      const current = window._CART_OPTIMISTIC_SEQ === seqAtPost;
      const cart = window.InstantCart && current ? window.InstantCart.stateFrom(data) : null;
      if (cart) {
        window._cartState = cart;
        data.__fullCart = cart;
      }

      /* the protection sync renderContents starts may need a tier swap; when
         it does, the attributes ride in that same write */
      if (!attributesWritten && window.ShippingProtection?.carryAttributes) {
        window.ShippingProtection.carryAttributes(attributes);
        attributesCarried = true;
      }

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // renderContents skips open() for 'add-to-cart-form' because stock Dawn
        // opens the drawer from the product form; this page has no such step, so
        // the add would otherwise refresh the drawer off-screen with no feedback
        await cartDrawer.renderContents(data, 'add-to-cart-form');
        adding = false;
        if (window.InstantCart) window.InstantCart.settle(instant, cart);
        else if (typeof updateCartAndProductJSON === 'function') updateCartAndProductJSON();
        // the drawer opened on the click; a shopper who has closed it since
        // meant it, so the server's answer does not slide it back in
        if (cartDrawer.classList.contains('active')) cartDrawer.open?.(button);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
      return true;
    } catch (error) {
      console.error('Benchmark PDP add to cart failed:', error);
      addOk = false;
      // leaving the drawer open on the empty state reads as "your cart really is
      // empty" — take the optimistic rows back out and close it, so the shopper is
      // returned to the modal they clicked from and can retry there
      if (instant) window.InstantCart.cancel(instant);
      clearAddingState(cartDrawer);
      adding = false;
      cartDrawer?.close?.();
      const status = Number(String(error.message).match(/\d{3}/)?.[0]);
      toast(
        status === 429
          ? 'Too many cart updates. Wait a moment and try again.'
          : 'Could not add to cart. Please try again.'
      );
      return false;
    } finally {
      // renderContents strips the skeletons on the success path; this covers any
      // route out of the try that did not go through either branch above
      if (adding) {
        if (instant) window.InstantCart.cancel(instant);
        clearAddingState(cartDrawer);
      }
      window._CART_ADD_IN_PROGRESS = false;
      button.disabled = false;
      /* attribution after the add, behind any protection sync the render
         started (one cart write at a time); checkout keeps waiting until it
         lands */
      if (!attributesWritten) {
        const sp = window.ShippingProtection;
        const spIdle = sp?.whenSynced?.() || Promise.resolve();
        const write = Promise.resolve(spIdle)
          .catch(() => {})
          .then(() => {
            // whatever the protection write did not carry goes on its own
            const left = attributesCarried ? sp.takeCarriedAttributes() : attributes;
            return left ? writeArmAttributesVerified(left) : null;
          });
        // attribution trails the add: it changes no line
        if (window.InstantCart) window.InstantCart.hold(write, { followUp: true });
      }
      // a held Check out tap is dropped, not replayed, when the add failed
      releaseCheckout(addOk);
    }
  };

  /* ---------- Founding Members upsell modal (figma 35026:23750) ---------- */
  /* GAH-BUNDLE-01: whether this visitor is in the no-bundle arm is read LIVE,
     never captured at init. Intelligems assigns the arm from an async module
     that lands AFTER this file has run, so anything decided here would be
     decided before the answer exists. The first build did exactly that and
     shipped a dead Add to Cart: the modal was CSS-hidden but still in the DOM,
     so the bind-time guard below skipped the button and the click did nothing. */
  const noBundleArm = () => document.documentElement.classList.contains('gd-no-bundle');
  /* GAH-UPSELL-01: the buy box Add to Cart skips the upsell modal in the
     no-bundle arm AND in the no-pop-up arm (html.gd-no-upsell). Read live at
     click time for the same reason as noBundleArm. Only the modal checks use
     this; everything else bundle-shaped stays on noBundleArm, because the
     no-pop-up arm keeps every bundle surface on the page. */
  const skipsFmModal = () => {
    // an Intelligems answer that landed since the last poll in theme.liquid
    // applies before the arm is read
    try {
      if (window.GD_FLAGS && window.GD_FLAGS.readUpsellIg) window.GD_FLAGS.readUpsellIg();
    } catch (e) {}
    return noBundleArm() || document.documentElement.classList.contains('gd-no-upsell');
  };

  /* Offer measurement: which surface an add came from, how often the upsell
     opens, and how it was dismissed (a backdrop tap, drag or Escape silently
     adds the simulator, so on orders it looks exactly like "No thanks").
     GD_FLAGS.track feeds GTM + Clarity, not Intelligems. Fire-and-forget and
     only ever after the add has started, so the instant cart's click-frame
     drawer renders first. Tags carry surface / reason / page only - never an
     arm (Clarity already holds those from the exposure functions). */
  const ATC_SURFACES = [
    ['.fm-modal__upgrade', 'modal_upgrade'],
    ['.fm-modal__decline', 'modal_decline'],
    ['.lb-cta[data-lb-sheet-close]', 'bundle_sheet'],
    ['.lb-cta', 'bundle_card'],
    ['.sticky-atc__solo', 'sticky_mobile'],
    ['.sb-cta', 'sticky_desktop'],
    ['.bb-atc', 'buybox'],
    ['.product-cta-benchmark__button', 'bottom_cta'],
  ];
  const atcSurface = (el) => {
    const hit = ATC_SURFACES.find(([sel]) => el.closest(sel));
    return hit ? hit[1] : 'other';
  };
  const trackOffer = (name, tags) => {
    if (window.GD_FLAGS && window.GD_FLAGS.track) {
      try {
        const page = (window._template && window._template.suffix) || 'default';
        window.GD_FLAGS.track(name, Object.assign({ gd_page: page }, tags));
      } catch (e) {}
    }
  };

  const fmModal = $('[data-fm-modal]');
  let closeFmModal = null;
  let dismissFmModal = null;
  let reopenFmModal = null;
  // true once Upgrade Now / No thanks has posted an add, so the close that
  // follows does not post the simulator on top of it
  let fmResolved = false;
  if (fmModal) {
    const card = $('.fm-modal__card', fmModal);
    const backdrop = $('.fm-modal__backdrop', fmModal);
    const sheetBody = $('[data-fm-sheet-body]', fmModal);
    const sheetHandle = $('[data-fm-sheet-handle]', fmModal);
    // matches the bottom-sheet breakpoint in benchmark-pdp.css
    const sheetQuery = window.matchMedia('(max-width: 989.98px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let lastFocus = null;
    let closeTimer = null;

    const open = () => {
      lastFocus = document.activeElement;
      clearTimeout(closeTimer);
      fmModal.hidden = false;
      // body{overflow:hidden} alone does not stop iOS touch panning — the
      // shared lock applies the fixed-body pattern on mobile instead
      if (window.BMScrollLock) BMScrollLock.lock('fm-modal');
      else document.body.style.overflow = 'hidden';
      if (sheetBody) sheetBody.scrollTop = 0;
      fmResolved = false;
      // let the hidden -> visible frame land before the transition starts
      requestAnimationFrame(() => requestAnimationFrame(() => fmModal.classList.add('is-open')));
      card?.focus({ preventScroll: true });
    };
    reopenFmModal = () => open();
    closeFmModal = () => {
      if (fmModal.hidden) return;
      fmModal.classList.remove('is-open');
      // The modal is still on screen for the length of its fade out, so
      // everything that hands the page back has to wait for the end of it:
      // unlocking on the first frame left a fully live page under a card that
      // was plainly still there. Same rule the cart drawer follows in
      // finishClose.
      const finish = () => {
        clearTimeout(closeTimer);
        fmModal.hidden = true;
        // a drag that ended in a dismiss leaves inline transform/opacity and
        // .is-dragging behind; clear them while the sheet is hidden so the
        // next open starts from the CSS translateY(100%) again
        fmModal.classList.remove('is-dragging');
        if (card) card.style.transform = '';
        if (backdrop) backdrop.style.opacity = '';
        if (window.BMScrollLock) BMScrollLock.unlock('fm-modal');
        else document.body.style.overflow = '';
        // preventScroll: releasing the lock re-seats the page itself, and a
        // refocus scroll on the same frame fights that restore
        if (lastFocus?.focus) lastFocus.focus({ preventScroll: true });
      };
      if (reduceMotion.matches) finish();
      else closeTimer = setTimeout(finish, 450);
    };

    /* ---- dismissing the upsell completes the plain add ----
       Paul (Aug 22 POV @2:07): tapping out of the pop-up "should just add the
       simulator to the cart". Nothing is in the cart at this point - the buy
       box Add to Cart is skipped by the [data-bm-atc] loop below and only
       opens this modal - so the dismiss has to post the add itself.
       Close first, then add on the next frame: addToCart opens the cart
       drawer synchronously and the drawer sits above this modal, so adding
       first drops the drawer onto a half-closed card. */
    const fmDismissIds = (fmModal.dataset.fmDismissAtc || '')
      .split(',')
      .map((token) => parseInt(token.trim(), 10))
      .filter(Boolean)
      .map((id) => ({ id }));

    // reason: 'backdrop' | 'drag' | 'escape', for the dismiss event only
    dismissFmModal = (reason) => {
      if (fmModal.hidden) return;
      const shouldAdd = !fmResolved && fmDismissIds.length && !window._CART_ADD_IN_PROGRESS;
      closeFmModal();
      if (!shouldAdd) return;
      fmResolved = true;
      // addToCart toggles button.disabled, so it needs an element; the decline
      // button is already on its way off screen, which is what we want
      const proxy = $('.fm-modal__decline', fmModal) || card;
      requestAnimationFrame(() => {
        addToCart(proxy, fmDismissIds);
        /* only a dismiss that really adds is counted: a tap during the fade
           after Upgrade / No thanks, or while an add is in flight, never gets
           here. The proxy is the decline button, so the surface is named here
           and never inferred inside addToCart. */
        trackOffer('gd_upsell_dismiss', { gd_upsell_reason: reason || 'other' });
      });
    };

    /* ---- drag the sheet down to dismiss (mobile only) ----
       A drag-away is the same resolution as a backdrop tap, so it goes
       through dismissFmModal: the shopper still leaves with the simulator
       in the cart. */
    if (card && sheetHandle) {
      let startY = 0;
      let delta = 0;
      let tracking = false;
      let dragging = false;
      let pointerId = null;
      // pointer capture retargets the click, so the sheet only takes the
      // gesture once the finger has clearly moved - taps stay taps
      const DRAG_SLOP = 8;

      // the handle always drags. Anywhere else on the card only drags when the
      // body has nothing to scroll: accepting scrollTop <= 0 alone let one
      // finger scroll the offer AND drag the sheet, which is most of what read
      // as "it still scrolls" on a sheet that fills the screen
      const canDragFrom = (target) =>
        sheetQuery.matches &&
        (sheetHandle.contains(target) ||
          (sheetBody
            ? sheetBody.scrollTop <= 0 && sheetBody.scrollHeight - sheetBody.clientHeight <= 1
            : true));

      const onMove = (e) => {
        if (!tracking) return;
        delta = Math.max(0, e.clientY - startY);
        if (!dragging) {
          if (delta < DRAG_SLOP) return;
          dragging = true;
          fmModal.classList.add('is-dragging');
          try {
            card.setPointerCapture?.(pointerId);
          } catch (_) {}
        }
        if (e.cancelable) e.preventDefault();
        card.style.transform = `translateY(${delta}px)`;
        if (backdrop) {
          const fade = Math.max(0, 1 - delta / (card.offsetHeight || 1));
          backdrop.style.opacity = String(fade);
        }
      };

      const endDrag = () => {
        if (!tracking) return;
        tracking = false;
        card.removeEventListener('pointermove', onMove);
        try {
          if (pointerId !== null && card.hasPointerCapture?.(pointerId)) {
            card.releasePointerCapture(pointerId);
          }
        } catch (_) {}
        pointerId = null;
        if (!dragging) return;
        dragging = false;
        fmModal.classList.remove('is-dragging');
        // past a quarter of the sheet it goes away, otherwise it springs back
        if (delta > Math.max(96, (card.offsetHeight || 0) * 0.25)) {
          // same resolution as the backdrop: dismissing completes the plain add
          dismissFmModal('drag');
        } else {
          card.style.transform = '';
          if (backdrop) backdrop.style.opacity = '';
        }
      };

      card.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || !canDragFrom(e.target)) return;
        tracking = true;
        dragging = false;
        startY = e.clientY;
        delta = 0;
        pointerId = e.pointerId;
        card.addEventListener('pointermove', onMove, { passive: false });
      });
      card.addEventListener('pointerup', endDrag);
      card.addEventListener('pointercancel', endDrag);
    }

    $$('[data-fm-modal-open]').forEach((button) => {
      button.addEventListener('click', (e) => {
        if (skipsFmModal()) return;   // no-bundle / no-pop-up arms add directly, no upsell
        e.preventDefault();
        open();
        // here, not in open(): open() also re-opens the modal after a failed add
        trackOffer('gd_upsell_open', {});
      });
    });
    $$('[data-fm-modal-close]', fmModal).forEach((el) =>
      el.addEventListener('click', () => dismissFmModal('backdrop'))
    );
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !fmModal.hidden) dismissFmModal('escape');
    });
  }

  /* the preview row is only instant if its thumbnail is already in cache -
     warm every button's crops at idle (a few KB each), so the first tap never
     shows an empty media box while the CDN answers */
  const warmPreviewImages = () => {
    const seen = new Set();
    $$('[data-bm-atc][data-bm-preview]').forEach((button) => {
      try {
        JSON.parse(button.dataset.bmPreview || '[]').forEach((p) => {
          [p.i160, p.i200].forEach((src) => {
            if (!src || seen.has(src)) return;
            seen.add(src);
            new Image().src = src;
          });
        });
      } catch (error) {
        /* malformed preview data warms nothing */
      }
    });
  };
  if ('requestIdleCallback' in window) requestIdleCallback(warmPreviewImages, { timeout: 4000 });
  else setTimeout(warmPreviewImages, 2500);

  /* the post-add upsell's tiles and cart-row thumb are lazy inside a hidden
     dialog, so they only start downloading on the tap that opens it (the row
     thumb used to ride in on the bundle sheet's thumbnails, which are lazy
     now). Warm them once the page has loaded - never for the no-bundle or
     no-pop-up arms, which never open the modal (read live, like everything
     GAH-BUNDLE-01), and never on Save-Data. Kept apart from warmPreviewImages,
     which belongs to the instant cart. */
  const warmFmModal = () => {
    try {
      if (!fmModal || skipsFmModal() || navigator.connection?.saveData) return;
      $$('img[loading="lazy"]', fmModal).forEach((img) => { img.loading = 'eager'; });
    } catch (e) {
      /* a failed warm only means the tiles load on open, as before */
    }
  };
  const scheduleFmWarm = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(warmFmModal, { timeout: 4000 });
    else setTimeout(warmFmModal, 1500);
  };
  if (document.readyState === 'complete') scheduleFmWarm();
  else window.addEventListener('load', scheduleFmWarm, { once: true });

  $$('[data-bm-atc]').forEach((button) => {
    // the buy box Add to Cart opens the upsell modal instead of adding directly;
    // without the modal in the DOM it falls through to a plain add.
    // The no-bundle and no-pop-up arms skip the modal, so they must fall through
    // too - but that is decided inside the click handler below, because the arm
    // is not known yet.
    const opensFmModal = fmModal && button.hasAttribute('data-fm-modal-open');

    // tokens are variant ids, optionally "id@sellingPlanId" for subscription
    // lines like the monthly coaching plan
    const ids = (button.dataset.bmAtc || '')
      .split(',')
      .map((token) => {
        const [rawId, rawPlan] = token.trim().split('@');
        const id = parseInt(rawId, 10);
        if (!id) return null;
        const sellingPlan = parseInt(rawPlan, 10);
        return sellingPlan ? { id, selling_plan: sellingPlan } : { id };
      })
      .filter(Boolean);
    if (!ids.length) {
      button.style.display = 'none';
      return;
    }

    /* ---- bundle grouping ----
       data-bm-bundle="<key>" tags every line this button posts so the cart
       drawer can draw them as ONE card: the first token is the parent the
       card is titled after, the rest hang under it as Included sub-rows.
       The lines stay real products, so price, the Recharge plan and
       fulfilment are exactly what they were; only the drawer's rendering
       changes. Underscore keys stay off the customer's order summary.
       Tagging also stops /cart/add.js merging these into a plain line of
       the same variant already in the cart, which is what we want: a bundle
       simulator and a loose simulator are different rows. */
    const bundleKey = button.dataset.bmBundle;
    if (bundleKey) {
      const parentId = ids[0].id;
      ids.forEach((item, index) => {
        item.properties =
          index === 0
            ? { _bm_bundle: bundleKey, _bm_bundle_role: 'main' }
            : {
                _bm_bundle: bundleKey,
                _bm_bundle_role: 'item',
                _bm_bundle_parent: String(parentId),
                _bm_bundle_order: String(index),
              };
      });
    }
    button.addEventListener('click', async (e) => {
      // the modal handler owns this click unless this visitor skips the modal
      // (GAH-BUNDLE-01 arm A, GAH-UPSELL-01 arm B): then fall through and add.
      if (opensFmModal && !skipsFmModal()) return;
      e.preventDefault();
      // set before the await: a backdrop tap while this request is in flight
      // must not queue the dismiss add on top of it
      const inModal = !!button.closest('[data-fm-modal]');
      if (inModal) fmResolved = true;
      // read before the add: addToCart ignores a tap while another add is in
      // flight, and that tap must not be counted as one
      const busy = !!window._CART_ADD_IN_PROGRESS;
      const adding = addToCart(button, ids);
      if (!busy) trackOffer('gd_atc_click', { gd_atc_surface: atcSurface(button) });
      // the drawer already shows the finished add on this frame, so the modal
      // goes down under it now - it used to fade out seconds later, and a
      // shopper who closed the drawer early landed back on it with a dead button
      const early = inModal && closeFmModal && window.InstantCart && window.InstantCart.ready();
      if (early) closeFmModal();
      const added = await adding;
      if (inModal && closeFmModal && added) closeFmModal();
      // a failed add hands the modal back as the place to retry
      else if (early && added === false && reopenFmModal) reopenFmModal();
    });
  });

  /* ---------- Buy-box FAQ: "see what coaching includes" ----------
     Desktop only in practice (the buy-box FAQ is hidden below 990px). Opens the
     coaching detail of the rail card in the SAME buy box through the card's own
     lb:open-item hook (benchmark-launch-bundle.js), never an add. The tile
     toggles, so an already-open coaching panel is only scrolled to, not shut. */
  $$('[data-faq-coach-open]').forEach((btn) => {
    btn.addEventListener('click', () => {
      try {
        if (noBundleArm()) return;
        const box = btn.closest('[data-buybox]') || btn.closest('.section-benchmark-pdp-main');
        const tile = box && box.querySelector('.lb-card--rail [data-lb-item="2"]');
        if (!tile) return;
        const card = tile.closest('[data-lb-card]');
        const dx = card && card.querySelector('[data-lb-dx]');
        const alreadyOpen = !!(dx && !dx.hidden && dx.querySelector('[data-lb-dxi="2"].on'));
        // lb:open-item, not tile.click(): a synthetic click would count in the
        // Intelligems "coaching tile opened" metrics (benchmark-launch-bundle.js)
        if (!alreadyOpen && card) card.dispatchEvent(new CustomEvent('lb:open-item', { detail: { idx: 2 } }));
        // the panel opens at the foot of the card; once its expand has run,
        // bring all of it into view even if the card top is off screen
        const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
        setTimeout(() => {
          if (dx && !dx.hidden) dx.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
        }, 400);
        // trackOffer comes from T-07; last and guarded, so the scroll above
        // still runs if that commit is ever backed out on its own
        if (typeof trackOffer === 'function') trackOffer('gd_faq_coach_details', {});
      } catch (e) {
        /* the link simply does nothing */
      }
    });
  });

  /* ---------- Rating → reviews: settle-corrected anchor jump ----------
     The scroll is instant: a smooth glide rides ~4000px past every reveal
     animation and lazy image, which reads as the page bouncing around. After
     landing we keep re-aligning until the async reviews content settles,
     handing control back the moment the user scrolls themselves. */
  $$('a[href="#reviews"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      /* GAH-PEEK-01: while a peek arm is rendered the buy-box rating opens the
         box under the stars instead (handled above). Read at click time. */
      if (link.hasAttribute('data-gd-peek-link') && document.documentElement.classList.contains('gd-peek')) return;
      /* GAH-COACH-01: while a coach-review arm is showing, this link goes to
         that review instead of the wall (Paul, Sep 8). Resolved at click
         time, never at bind time: Intelligems lands the arm class seconds
         after first paint, and the section renders once per arm so only the
         copy that is actually displayed can match. In the control arm
         nothing is showing and this is the wall, exactly as today. The
         settle-correction below then aligns to that section's own
         scroll-margin-top instead of the anchor's. */
      const coach = $$('.coach-review').find((el) => el.offsetParent !== null);
      const target = coach || document.getElementById('reviews');
      if (!target) return;
      e.preventDefault();
      let cancelled = false;
      const cancel = () => { cancelled = true; };
      window.addEventListener('wheel', cancel, { once: true, passive: true });
      window.addEventListener('touchstart', cancel, { once: true, passive: true });
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      // only the wall is a documented anchor; the coach section keeps the URL clean
      if (!coach) history.replaceState(null, '', '#reviews');
      const wantTop = () => parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      let lastY = -1;
      let tries = 0;
      const iv = setInterval(() => {
        tries += 1;
        if (cancelled || tries > 10) { clearInterval(iv); return; }
        const settled = Math.abs(window.scrollY - lastY) < 2;
        lastY = window.scrollY;
        if (!settled) return; // still gliding
        const off = target.getBoundingClientRect().top - wantTop();
        if (Math.abs(off) <= 4) { clearInterval(iv); return; }
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }, 300);
    });
  });

  /* ---------- GAH-RANGE-01: PracticeCam (snippets/benchmark-pdp-practicecam) ----------
     One clip today (the swing analysis with the tracer drawn on), and the
     code still takes more. Below 990px the clips share one stage and play
     back to back: only the clip that is on has a playing video, its pill
     fills off currentTime, and a pill tap jumps. From 990px they loop side
     by side. A clip gets a src only when the band comes within 200px of the
     viewport and pauses off screen; it always takes the 540p file. A
     data-practicecam-fast range ("5-13.5") plays that stretch at RATE, for
     a clip that holds a pose while something slow happens.
     A refused play() is swallowed: in-app browsers and Low Power Mode block
     autoplay, and the picked poster is the card then (the pills still swap
     posters, and a pill tap is a gesture, so it starts the clip). No retry
     on the next gesture: in TikTok and the native app that first tap is what
     opens the forced fullscreen player. Reduced motion gets posters only.
     A card tap opens the dialog and starts the 720p file inside the tap; a
     play() from a later callback is outside the gesture and strict webviews
     refuse it. The clips have no audio track, so muted costs nothing and
     keeps play() allowed everywhere.
     Events (GD_FLAGS.track, so dataLayer + Clarity):
       gah_practicecam_view    the clips came on screen, once per pageview
       gah_practicecam_switch  a stage pill was tapped; gah_practicecam_clip
       gah_practicecam_open    a card or a dialog tab was tapped;
                               gah_practicecam_clip = swing | tracer,
                               gah_practicecam_via = card | tab */
  const camSec = $('.bm-pdp [data-practicecam]');
  if (camSec) {
    const wrap = $('.practicecam-cards', camSec);
    const cards = $$('[data-practicecam-open]', camSec);
    const segs = $$('[data-practicecam-seg]', camSec);
    const fills = segs.map((seg) => $('em', seg));
    const pop = $('[data-practicecam-pop]', camSec);
    const popVideo = pop && $('[data-practicecam-pop-video]', pop);
    const tabs = pop ? $$('[data-practicecam-tab]', pop) : [];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stacked = window.matchMedia('(max-width: 989.98px)');
    const hasDialog = !!pop && typeof pop.showModal === 'function';
    const RATE = 2.5;
    const track = (name, tags) => {
      const flags = window.GD_FLAGS;
      if (flags && flags.track) { flags.track(name, tags); return; }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, tags || {}));
    };
    let onScreen = false;
    let current = 0;
    let lastCard = null;

    const inline = (card) => $('video', card);
    const isOpen = () => !!pop && pop.hasAttribute('open');
    const load = (card) => {
      const video = inline(card);
      if (video.getAttribute('src')) return;
      video.preload = 'auto';
      video.src = card.dataset.practicecamSrcSm;
    };
    const playInline = () => {
      if (reduced.matches || !onScreen || isOpen()) return;
      cards.forEach((card, i) => {
        const video = inline(card);
        if (stacked.matches && i !== current) { video.pause(); return; }
        load(card);
        /* stacked, a clip has to end so the next one can take the stage;
           a lone clip just loops */
        video.loop = !stacked.matches || cards.length === 1;
        video.muted = true;
        video.defaultMuted = true;
        video.play().catch(() => {});
      });
    };
    const pauseInline = () => cards.forEach((card) => inline(card).pause());
    /* only the clip on the stage shows progress */
    const clearFills = () => fills.forEach((fill, i) => { if (fill && i !== current) fill.style.transform = ''; });
    const show = (index) => {
      current = index;
      cards.forEach((card, i) => card.classList.toggle('is-on', i === index));
      segs.forEach((seg, i) => seg.setAttribute('aria-pressed', String(i === index)));
      clearFills();
      try { inline(cards[index]).currentTime = 0; } catch (e) {}
      playInline();
    };

    cards.forEach((card, i) => {
      const video = inline(card);
      const fast = (card.dataset.practicecamFast || '').split('-').map(Number);
      const next = (i + 1) % cards.length;
      video.addEventListener('timeupdate', () => {
        if (fast.length === 2 && fast[1] > fast[0]) {
          const rate = video.currentTime >= fast[0] && video.currentTime < fast[1] ? RATE : 1;
          if (video.playbackRate !== rate) video.playbackRate = rate;
        }
        /* a pause fires one more timeupdate, so a clip that just left the
           stage must not repaint its pill */
        if (fills[i] && i === current && video.duration) fills[i].style.transform = `scaleX(${Math.min(1, video.currentTime / video.duration)})`;
      });
      /* the next clip is fetched once this one is running, never before */
      video.addEventListener('playing', () => { if (stacked.matches && next !== i) load(cards[next]); });
      video.addEventListener('ended', () => { if (stacked.matches) show(next); });
    });
    segs.forEach((seg, i) => seg.addEventListener('click', () => {
      show(i);
      track('gah_practicecam_switch', { gah_practicecam_clip: cards[i].dataset.practicecamName });
    }));
    /* crossing 990px swaps one stage for two loops; older Safari has no
       addEventListener on a media query list */
    if (stacked.addEventListener) stacked.addEventListener('change', () => { clearFills(); playInline(); });

    if (wrap && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          onScreen = entry.isIntersecting;
          if (onScreen) playInline(); else pauseInline();
        });
      }, { rootMargin: '200px 0px' }).observe(wrap);
      const seen = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        seen.disconnect();
        track('gah_practicecam_view');
      }, { threshold: 0.4 });
      seen.observe(wrap);
    }

    if (pop && popVideo) {
      const popShow = (index, via) => {
        const card = cards[index];
        if (!card) return;
        tabs.forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === index)));
        popVideo.poster = card.dataset.practicecamPoster;
        popVideo.setAttribute('aria-label', card.dataset.practicecamDesc || '');
        popVideo.src = card.dataset.practicecamSrc;
        popVideo.muted = true;
        popVideo.play().catch(() => {});
        track('gah_practicecam_open', { gah_practicecam_clip: card.dataset.practicecamName, gah_practicecam_via: via });
      };
      /* runs once per close, from the dialog's own close event (Esc, the X,
         the backdrop, pagehide) or straight from popClose without <dialog> */
      const popCleanup = () => {
        popVideo.pause();
        popVideo.removeAttribute('src');
        popVideo.load();
        if (window.BMScrollLock) window.BMScrollLock.unlock('practicecam-pop');
        if (lastCard) lastCard.focus({ preventScroll: true });
        playInline();
      };
      const popClose = () => {
        if (!isOpen()) return;
        if (hasDialog) { pop.close(); return; }
        pop.removeAttribute('open');
        popCleanup();
      };
      const popOpen = (index) => {
        lastCard = cards[index];
        pauseInline();
        if (hasDialog) pop.showModal(); else pop.setAttribute('open', '');
        if (window.BMScrollLock) window.BMScrollLock.lock('practicecam-pop');
        popShow(index, 'card');
      };

      if (hasDialog) pop.addEventListener('close', popCleanup);
      else document.addEventListener('keydown', (e) => { if (e.key === 'Escape') popClose(); });
      cards.forEach((card, i) => card.addEventListener('click', () => popOpen(i)));
      tabs.forEach((tab, i) => tab.addEventListener('click', () => popShow(i, 'tab')));
      const popX = $('[data-practicecam-pop-close]', pop);
      if (popX) popX.addEventListener('click', popClose);
      /* the dialog fills the viewport, so a tap on the dark area lands on the
         dialog itself or the centring column, never on the stage or tabs */
      pop.addEventListener('click', (e) => {
        if (e.target === pop || e.target.classList.contains('practicecam-pop__inner')) popClose();
      });
      /* bfcache would bring the page back with the dialog still open while
         BMScrollLock.sanitize() drops the lock, so close it on the way out */
      window.addEventListener('pagehide', popClose);
    }
  }
})();
